/*! towsons-personaliser.js — live personalisation preview on the storefront.
 *
 * Draws the product's design template in the shopper's browser with the same
 * Fabric build and tcd-render.js the portal's designer and batch renderer use,
 * so what the shopper sees is what gets printed. Everything it needs is public
 * data: the `towsons.personaliser` metafield (inlined by the snippets), the
 * template bundle in the store's Files library, Google Fonts for Google
 * families and the mug photos. It never calls the portal.
 *
 * Three jobs, one file:
 *   • product page — the fields, the live preview drawn into the product
 *     images (or an in-card canvas), the hidden property the order carries;
 *   • basket (drawer and cart page) — each personalised line's image becomes
 *     its own preview, instantly from a session cache written at add-to-basket
 *     or re-rendered from the line's properties; plus an "Edit" link;
 *   • edit mode — the product page opened from that link pre-fills the
 *     fields, the button reads "Update basket", and the old line is removed
 *     when the new one is added.
 *
 * The inputs are ordinary cart line-item properties and work with no JS at
 * all; everything else is progressive enhancement. Vanilla JS, no bundler.
 */
(function () {
  'use strict';
  // Once a page. The script is on a page several times — the product block,
  // each basket line, the discover cards, and (Horizon) the loader for the
  // basket on every page — and every copy of a <script src> runs: the first
  // sets everything up (its init follows later mounts), the rest stop here.
  if (window.TowsonsPersonaliser && window.TowsonsPersonaliser.init) return;

  var LOADED = {};
  function loadScript(src) {
    if (!LOADED[src]) {
      LOADED[src] = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = resolve;
        s.onerror = function () { reject(new Error('failed to load ' + src)); };
        document.head.appendChild(s);
      });
    }
    return LOADED[src];
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image failed: ' + url)); };
      img.src = url;
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // THE FREEBIE RISES OUT OF THE MUG.
  //
  // Pick a treat and its picture pops up out of the rim — half of it showing,
  // the rest inside — on the product page, in the basket's edit modal, on the
  // basket line and in the preview file the order keeps. The picture is the
  // treat's cutout (background keyed out at publish); the place is the
  // photo's treat spot, or a guess from the artwork box.
  // ─────────────────────────────────────────────────────────────────────
  var TREAT_IMAGES = window.__towsonsTreatImages || (window.__towsonsTreatImages = {});
  function loadTreat(url) {
    if (!url) return Promise.resolve(null);
    if (!TREAT_IMAGES[url]) TREAT_IMAGES[url] = loadImage(url).catch(function () { delete TREAT_IMAGES[url]; return null; });
    return TREAT_IMAGES[url];
  }
  // THE MUG'S MOUTH, READ OFF THE PHOTO.
  //
  // Mug photos are cut-outs, so the silhouette says where the mug is. Its
  // topmost row is the rim's BACK edge; from there the rim ellipse widens
  // until it meets the body, and that row — the front lip — is what a treat
  // comes out of. Derived from the artwork box instead, the guess landed 73px
  // above the mug on the 11oz shot (the print starts level with the rim) and
  // the packet floated in the air.
  //
  // Shared with the mockup editor's bar (_rimFromRgba there); the two are
  // pinned to the same numbers by test/unit/treatPopOut.test.js.
  function rimFromRgba(data, w, h) {
    var spans = [], y, x, first, last, i;
    // edge[x] = the mug's topmost row in that column, -1 where there is no
    // mug: the outline a treat goes behind.
    var edge = new Int32Array(w);
    for (x = 0; x < w; x++) edge[x] = -1;
    for (y = 0; y < h; y++) {
      first = -1; last = -1;
      for (x = 0; x < w; x++) {
        i = (y * w + x) * 4 + 3;
        if (data[i] > 40) { if (first < 0) first = x; last = x; if (edge[x] < 0) edge[x] = y; }
      }
      if (first >= 0) spans.push({ y: y, first: first, last: last, w: last - first + 1 });
      else if (spans.length) break;   // past the bottom of a floating cut-out
    }
    if (spans.length < 3) return null;
    // The rim ellipse lives in the top of the mug; its widest row is the lip.
    // The body's width, sampled just below the rim — far enough that the
    // ellipse has finished opening, close enough that the HANDLE has not
    // started (on the 11oz shot the handle makes the silhouette 1031px wide
    // against a 764px body, and a widest-wins rule put the rim on it).
    var probe = Math.min(spans.length - 1, Math.max(3, Math.round(spans.length * 0.06)));
    var bodyW = spans[probe].w;
    var lip = spans[probe];
    for (i = 0; i < probe; i++) if (spans[i].w >= bodyW * 0.97) { lip = spans[i]; break; }
    // The opening is the mug's mouth, not its outside: a little narrower.
    // `natural`: these are the PHOTO's own pixels, not the authored overlay's.
    return { x: (lip.first + lip.last) / 2, y: lip.y, w: lip.w * 0.86, edge: edge, natural: true };
  }
  // Read once per mug photo and kept on it.
  function rimOf(m) {
    if (!m || !m.img) return null;
    if (m.__towsonsRim !== undefined) return m.__towsonsRim;
    m.__towsonsRim = null;
    try {
      var img = m.img, w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
      if (w && h) {
        var cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        var ctx = cv.getContext('2d');
        ctx.drawImage(img, 0, 0);
        m.__towsonsRim = rimFromRgba(ctx.getImageData(0, 0, w, h).data, w, h);
      }
    } catch (e) { /* a photo on white, or a tainted canvas: fall back below */ }
    return m.__towsonsRim;
  }
  // Where the treat pops out of this mug photo: the rim's centre, the rim
  // line and the opening's width, photo px. The photo's own spot when the
  // editor set one, else the mug's own mouth, else — for a photo with no
  // cut-out to read — a little above the print area.
  function treatSpotFor(m, view) {
    // The mug's outline belongs to the photo, so a spot set by hand still
    // goes behind it.
    var rim = rimOf(view || (m && m.img ? m : null));
    if (m && m.treat && m.treat.w > 0) {
      return { x: m.treat.x, y: m.treat.y, w: m.treat.w, edge: rim ? rim.edge : null };
    }
    if (rim) return rim;
    var o = m && m.overlay;
    if (!o) return null;
    return { x: o.x + o.w / 2, y: o.y - o.h * 0.12, w: o.w * 0.85 };
  }
  // The pop: quick, a little past the mark, settling back. The overshoot is
  // how far past half-out it goes, and what the treat is sized to fit at.
  var MAX_RISE = 1.2;
  function easeOutBack(t) { var c1 = 1.70158, c3 = c1 + 1; return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2); }
  // `rise` 0 = fully inside the mug (nothing to see), 1 = half out. Clipped
  // at the rim line, so what is below it is inside the mug.
  function drawTreat(ctx, treat, spot, ps, px, py, geomScale, cssW, cssH) {
    if (!treat || !treat.img || !(treat.rise > 0) || !spot) return;
    var img = treat.img, iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    // A spot read off the photo is already in the photo's pixels; one set in
    // the editor is in the authored overlay's, which a differently sized CDN
    // copy scales.
    var k = spot.natural ? 1 : geomScale;
    var cx = px + spot.x * k * ps, rimY = py + spot.y * k * ps, sw = spot.w * k * ps;
    var tw = sw * 0.72, th = tw * ih / iw;
    // WHAT STICKS UP HAS TO FIT. A Galaxy sachet is three times taller than
    // the mug's mouth is wide, and sized on width alone it ran off the top of
    // the frame and was cut straight through. Two limits: never more than
    // half the frame tall, and never taller than twice the room above the rim
    // — at rest half of it shows, and that half has to land inside the frame.
    var cap = cssH * 0.55;
    var room = Math.max(0, rimY - cssH * 0.015) * 2;
    var fit = Math.min(th, cap, room);
    if (fit < th) { th = fit; tw = th * iw / ih; }
    var rise = Math.min(MAX_RISE, Math.max(0, treat.rise));
    // The overshoot is a moment, not a size: it stops at the frame's edge
    // rather than shrinking every treat to leave room for it.
    var top = Math.max(0, rimY - rise * th / 2);
    var x0 = cx - tw / 2, x1 = cx + tw / 2;
    ctx.save();
    ctx.beginPath();
    // BEHIND THE RIM, NOT ACROSS IT. Clipped on a straight line the packet
    // cut through the coloured rim band of an inner-and-handle mug. The mug's
    // own top edge is the boundary: the treat disappears behind it, the rim
    // stays whole, and the shoulders tuck in where the rim curves away.
    if (spot.edge && spot.edge.length) {
      var e = spot.edge, sx, ey;
      ctx.moveTo(x0, 0);
      for (var X = Math.floor(x0); X <= Math.ceil(x1); X++) {
        sx = Math.round((X - px) / ps);
        ey = (sx >= 0 && sx < e.length && e[sx] >= 0) ? py + e[sx] * ps : rimY;
        ctx.lineTo(X, ey);
      }
      ctx.lineTo(x1, 0);
      ctx.closePath();
    } else {
      ctx.rect(0, 0, cssW, Math.max(0, rimY));
    }
    ctx.clip();
    // The small treat photos are upscaled here, so ask for the good filter.
    ctx.imageSmoothingEnabled = true;
    try { ctx.imageSmoothingQuality = 'high'; } catch (err) { /* not everywhere */ }
    ctx.drawImage(img, x0, top, tw, th);
    ctx.restore();
  }

  function parseJsonAttr(el, name) {
    try { return JSON.parse(el.getAttribute(name) || 'null'); } catch (e) { return null; }
  }

  // Small stable hash for cache keys (djb2).
  function hash(str) {
    var h = 5381;
    for (var i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
  }

  function compactValues(values) {
    var out = {};
    Object.keys(values || {}).sort().forEach(function (k) { if (values[k] !== '' && values[k] != null) out[k] = values[k]; });
    return out;
  }
  // Keyed by the blank as well as the words: "Susana" on a red mug and on a
  // pink mug are two pictures, and a basket line whose colour was changed
  // must not be handed the frame it had before.
  // `size`: a frame drawn smaller than the usual 360 (the "More colours" mugs)
  // is kept apart from one a basket line would show.
  function previewCacheKey(templateId, changeId, values, view, blank, treat, size) {
    return 'towsons-preview4:' + hash(String(templateId) + '|' + String(changeId) + '|' + JSON.stringify(compactValues(values)) + '|' + (view || 0) + '|' + (blank || '') + '|' + (treat || '') + (size && size !== 360 ? '|' + size : ''));
  }
  function cacheGet(key) { try { return window.sessionStorage ? sessionStorage.getItem(key) : null; } catch (e) { return null; } }
  function cacheSet(key, value) { try { if (window.sessionStorage) sessionStorage.setItem(key, value); } catch (e) { /* full or blocked */ } }

  // The renderer's "smile" bend (MockupGenerator.applyCylindricalDistortion):
  // each column of the design drops by amp·sin(π·x/w)·h, edges fixed, centre
  // pushed down. Drawn as 1-device-pixel column slices.
  function drawCylindrical(ctx, src, sx, sy, sw, sh, dx, dy, dw, dh, amp) {
    if (!(amp > 0)) { ctx.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh); return; }
    var cols = Math.max(1, Math.round(dw));
    var colW = dw / cols, srcColW = sw / cols;
    for (var i = 0; i < cols; i++) {
      var nx = cols > 1 ? i / (cols - 1) : 0.5;
      var drop = amp * Math.sin(Math.PI * nx) * dh;
      ctx.drawImage(src, sx + i * srcColW, sy, Math.max(srcColW, 0.5), sh, dx + i * colW, dy + drop, Math.max(colW, 0.5) + 0.35, dh);
    }
  }

  function canvasToBlob(cv, quality) {
    return new Promise(function (resolve) {
      try { cv.toBlob(function (b) { resolve(b || null); }, 'image/jpeg', quality || 0.86); } catch (e) { resolve(null); }
    });
  }

  function cartUrl(name) {
    var r = window.routes || {};
    var map = { add: r.cartAddUrl || '/cart/add', change: r.cartChangeUrl || '/cart/change', cart: r.cartUrl || '/cart' };
    return map[name] + '.js';
  }

  // Infinite Options (the app this replaces) injects "#infiniteoptions-container"
  // into the product form from a script tag whenever its app block is not in
  // the theme. On a personalised product ours is the only set of fields, so
  // the container goes and stays gone; its bound validation then has nothing
  // left to refuse.
  function evictOptionsApp(form) {
    var scope = form || document;
    var kill = function () {
      var els = scope.querySelectorAll('#infiniteoptions-container, .infiniteoptions-no-atc-warning-wrapper');
      for (var i = 0; i < els.length; i++) els[i].parentNode && els[i].parentNode.removeChild(els[i]);
      return els.length;
    };
    kill();
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function () { kill(); });
      mo.observe(scope === document ? document.body : scope, { childList: true, subtree: true });
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Renderer — one per template bundle, shared by every surface on the page.
  // Loads Fabric + tcd-render, the bundle, its fonts and the mug photos, then
  // lays values out and composites onto a view. Fonts come the way the
  // renderer gets them: Google families from Google Fonts (the portal's stored
  // copies are picker subsets), uploaded faces from the Files library.
  // ─────────────────────────────────────────────────────────────────────
  // On the window: the theme redraws the block this script sits in when the
  // variant changes, which re-runs the file. A fresh cache would re-fetch the
  // bundle, the mug photos and the fonts on every colour change.
  var RENDERERS = window.__towsonsRenderers || (window.__towsonsRenderers = {});
  function Renderer(cfg, scripts) {
    this.cfg = cfg;
    this.scripts = scripts;
    this.views = [];
    this.ready = false;
  }
  // WHICH MUG THIS COLOUR IS DRAWN ON.
  //
  // The portal publishes one mockup per blank and a map from the shop's own
  // variant ids to them. Before that there was a single mockup — the listing's
  // first variant by id — so typing a name drew it on that blank whatever the
  // shopper had selected: land on Black, type "Danny", see a white mug.
  // Falls back to cfg.mockup, which is what a product published by an older
  // portal still carries.
  function blankFor(cfg, variantId) {
    var map = cfg && cfg.variant_blanks;
    if (!map || !variantId) return null;
    return map[String(variantId)] || null;
  }
  function viewsFor(cfg, blankSku) {
    if (blankSku && cfg.mockups && cfg.mockups[blankSku]) return cfg.mockups[blankSku].views || [];
    return (cfg.mockup && cfg.mockup.views) || [];
  }

  // THE OTHER COLOURS, FETCHED BEFORE THEY ARE ASKED FOR.
  //
  // Each colour's renderer loads that blank's mug photos on first use, so the
  // first switch to a colour stalls while two images come down. The bundle,
  // the scene and the fonts are shared and already in hand by then — only the
  // photos differ — so the rest can be warmed quietly once the one the shopper
  // is actually looking at has been drawn.
  //
  // Deliberately after first paint and one at a time: the point is that
  // switching feels instant, not that the page has more to do before it shows
  // anything.
  function warmOtherBlanks(cfg, scripts, currentBlank) {
    var skus = Object.keys(cfg && cfg.mockups || {}).filter(function (k) { return k !== currentBlank; });
    if (!skus.length) return;
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 250); };
    var i = 0;
    var next = function () {
      if (i >= skus.length) return;
      var sku = skus[i++];
      var key = cfg.bundle_url + '|' + sku;
      if (RENDERERS[key]) { next(); return; }
      var r = new Renderer(cfg, scripts);
      r.blankSku = sku;
      // Cached on the promise, so the shopper switching mid-warm waits on the
      // SAME load rather than starting a second one.
      RENDERERS[key] = r.load().then(function () { return r; });
      RENDERERS[key].then(function () { idle(next); }, function () {
        // A blank whose photos will not load must not stop the others, and
        // must not be remembered as loaded — Renderer.get will try it again.
        delete RENDERERS[key];
        idle(next);
      });
    };
    idle(next);
  }

  // Already loaded, so there is nothing to wait for and nothing to announce.
  Renderer.has = function (cfg, variantId) {
    if (!cfg || !cfg.bundle_url) return false;
    return !!RENDERERS[cfg.bundle_url + '|' + (blankFor(cfg, variantId) || '_default')];
  };

  // A SMALLER COPY OF THE MUG PHOTOS, for a picture drawn small (the "More
  // colours" mugs, 40px). The full photos are ~700 KB each; the shop's CDN
  // serves any width. The words are placed in the photo's own pixels, so a
  // smaller copy is used only when the published views say how big the photo
  // really is (view.w) — compositeInto then scales the placing along. Without
  // that, the full photo: right, if heavier.
  function photoWidthFor(cfg, blank, opts) {
    var want = Number(opts && opts.photoWidth) || 0;
    if (!want) return 0;
    var views = viewsFor(cfg, blank);
    // Only the photos that will be loaded need their size: the first, for a
    // picture of the first view alone.
    if (opts && opts.firstViewOnly) views = views.slice(0, 1);
    return views.length && views.every(function (v) { return Number(v.w) > want; }) ? want : 0;
  }
  function sizedUrl(url, width) {
    return width ? url + (String(url).indexOf('?') < 0 ? '?' : '&') + 'width=' + width : url;
  }
  Renderer.get = function (cfg, scripts, variantId, opts) {
    var blank = blankFor(cfg, variantId);
    var small = photoWidthFor(cfg, blank, opts);
    // A small picture of the first view only (the "More colours" mugs) loads
    // that view's photo alone: the other is never drawn there.
    var first = !!(small && opts && opts.firstViewOnly);
    // Keyed by the blank as well as the bundle: the scene, its fonts and the
    // bundle are the same for every colour, only the mug photos differ, so a
    // colour the shopper has already looked at costs nothing to go back to.
    var key = cfg.bundle_url + '|' + (blank || '_default') + (small ? '|w' + small : '') + (first ? '|v0' : '');
    if (!RENDERERS[key]) {
      var r = new Renderer(cfg, scripts);
      r.blankSku = blank;
      r.photoWidth = small;
      r.firstViewOnly = first;
      RENDERERS[key] = r.load().then(function () { return r; });
    }
    return RENDERERS[key];
  };
  // ONE DOWNLOAD OF A BUNDLE, however many renderers want it: the "More
  // colours" box starts one renderer per colour, all at once, and each used to
  // fetch the same file. Kept on window, like the renderers (the file re-runs
  // on a variant change); one that failed is forgotten, so the next renderer
  // asks again. towsons-more-colours.js starts the download early through the
  // same map, before this file has even arrived.
  var BUNDLES = window.__towsonsBundles || (window.__towsonsBundles = {});
  function bundleFor(url) {
    if (!BUNDLES[url]) {
      var p = BUNDLES[url] = fetch(url, { mode: 'cors', credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('bundle HTTP ' + r.status);
        return r.json();
      });
      p.catch(function () { if (BUNDLES[url] === p) delete BUNDLES[url]; });
    }
    return BUNDLES[url];
  }
  // Everything a renderer needs, side by side rather than one after another
  // (4 Oct 2026, Philip: "if you can tune up the speed of it showing"). The
  // two scripts together — tcd-render's helpers are handed fabric when they
  // are called, so it need not wait for fabric to arrive — and the fonts as
  // soon as the bundle names them, alongside the scripts and the photos. On a
  // phone that was four waits in a row; now it is the longest of them.
  Renderer.prototype.load = function () {
    var self = this, cfg = this.cfg;
    var scriptsP = Promise.all([
      window.fabric ? null : loadScript(this.scripts.fabric),
      window.TcdRender ? null : loadScript(this.scripts.tcd),
    ]);
    var bundleP = bundleFor(cfg.bundle_url);
    var fontsP = bundleP.then(function (b) { return self.loadFonts(b); });
    var views = viewsFor(cfg, this.blankSku);
    if (this.firstViewOnly) views = views.slice(0, 1);
    var viewsP = Promise.all(views.map(function (v) {
      return loadImage(sizedUrl(v.url, self.photoWidth)).then(function (img) { return { meta: v, img: img }; }, function () { return null; });
    }));
    return Promise.all([scriptsP, bundleP, viewsP, fontsP]).then(function (r) {
      self.bundle = r[1];
      self.views = r[2].filter(Boolean);
      return self.setupScene();
    }).then(function () { self.ready = true; });
  };
  // Text the faces must be able to draw before we trust them: the renderer
  // probes with the same idea. A face "loads" the instant its stylesheet is
  // parsed, but asking before that matches nothing and resolves at once — the
  // frame then draws with a fallback and, worse, gets cached. So: wait for the
  // stylesheet, ask with real text, then measure each face against the
  // generic fallbacks until it differs, or give up after a few seconds.
  var FONT_SAMPLE = 'WARNING Danny #Sample 1234 WMil';
  // THE PAGE'S OWN WORD FIRST (TKT9F82C, 22 Oct 2026). A face built to the
  // same widths as the fallback — Arimo is Arial's, and Arial is Helvetica's,
  // the sans-serif of every Apple device — measures 687.62px against 687.64px:
  // the width test could never tell it had arrived, so every page waited out
  // all 24 tries (6 s) and then cached nothing. The font set says it outright:
  // a face of that family whose file has LOADED (not merely declared — its
  // status stays "unloaded" until the file is in).
  function faceLoaded(spec) {
    try {
      var set = document.fonts;
      if (!set || typeof set.forEach !== 'function') return false;
      var fam = spec.replace(/^.*px\s*/, '').replace(/["']/g, '').trim().toLowerCase();
      var hit = false;
      set.forEach(function (f) {
        if (!hit && f && f.status === 'loaded' && String(f.family || '').replace(/["']/g, '').trim().toLowerCase() === fam) hit = true;
      });
      return hit;
    } catch (e) { return false; }
  }
  function faceApplied(spec) {
    if (faceLoaded(spec)) return true;
    try {
      var ctx = document.createElement('canvas').getContext('2d');
      if (!ctx) return true;
      ctx.font = spec; var w = ctx.measureText(FONT_SAMPLE).width;
      var fam = spec.replace(/^.*px\s*/, '');
      ctx.font = spec.replace(fam, 'serif'); var ws = ctx.measureText(FONT_SAMPLE).width;
      ctx.font = spec.replace(fam, 'sans-serif'); var wss = ctx.measureText(FONT_SAMPLE).width;
      return Math.abs(w - ws) > 0.5 && Math.abs(w - wss) > 0.5;
    } catch (e) { return true; }
  }
  function stylesheetLoaded(href, marker) {
    var link = document.querySelector('link[' + marker + '="' + href + '"]');
    if (link) return link.__towsonsLoaded || Promise.resolve();
    link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = href;
    link.setAttribute(marker, href);
    link.__towsonsLoaded = new Promise(function (resolve) {
      var done = function () { resolve(); };
      link.onload = done; link.onerror = done;
      setTimeout(done, 8000);
    });
    document.head.appendChild(link);
    return link.__towsonsLoaded;
  }
  // The faces asked for and checked once per bundle, not once per renderer —
  // four colours drawn at once added each face four times and ran four checks.
  // Only faces that applied are remembered: ones that did not are asked for
  // again by the next renderer (a font that was slow is not written off).
  var FONTS = window.__towsonsFonts || (window.__towsonsFonts = {});
  Renderer.prototype.loadFonts = function (bundle) {
    var self = this, key = (this.cfg && this.cfg.bundle_url) || '';
    var p = key && FONTS[key];
    if (!p) {
      p = fontsReady(bundle);
      if (key) {
        FONTS[key] = p;
        p.then(function (ok) { if (!ok && FONTS[key] === p) delete FONTS[key]; });
      }
    }
    return p.then(function (ok) { self.fontsOk = ok; });
  };
  // → Promise<boolean>: whether every face the bundle names now draws.
  function fontsReady(bundle) {
    var fontsOk = false;
    var fonts = bundle.fonts || [], google = bundle.google || [];
    var faceSpecs = [];
    var ready = bundle.google_css ? stylesheetLoaded(bundle.google_css, 'data-towsons-google-fonts') : Promise.resolve();
    google.forEach(function (g) { faceSpecs.push((g.italic ? 'italic ' : '') + String(g.weight || 400) + ' 40px "' + g.family + '"'); });
    if ('FontFace' in window) {
      fonts.forEach(function (f) {
        try {
          var face = new FontFace(f.family, 'url(' + f.url + ')', { weight: String(f.weight || 400), style: f.italic ? 'italic' : 'normal' });
          document.fonts.add(face);
          faceSpecs.push((f.italic ? 'italic ' : '') + String(f.weight || 400) + ' 40px "' + f.family + '"');
        } catch (e) { /* fallback metrics for this face */ }
      });
    }
    var askAll = function () {
      return Promise.all(faceSpecs.map(function (spec) {
        return (document.fonts && document.fonts.load) ? document.fonts.load(spec, FONT_SAMPLE).catch(function () {}) : Promise.resolve();
      }));
    };
    var attempts = 0;
    var verify = function () {
      var missing = faceSpecs.filter(function (spec) { return !faceApplied(spec); });
      if (!missing.length || attempts >= 24) {
        fontsOk = !missing.length;
        if (missing.length && window.console && console.warn) console.warn('[towsons-personaliser] fonts not applied: ' + missing.join(', '));
        return null;
      }
      attempts++;
      return new Promise(function (r) { setTimeout(r, 250); }).then(askAll).then(verify);
    };
    return ready.then(askAll).then(function () { return document.fonts ? document.fonts.ready : null; }).then(verify).then(function () {
      // Fabric remembers glyph widths per family; anything measured before the
      // real face arrived would stick, so start clean.
      try { if (window.fabric && window.fabric.cache && typeof window.fabric.cache.clearFontCache === 'function') window.fabric.cache.clearFontCache(); } catch (e) {}
      return fontsOk;
    });
  }
  Renderer.prototype.setupScene = function () {
    var self = this, b = this.bundle, fabric = window.fabric, T = window.TcdRender;
    if (!T.__effectsInstalled) { T.installTextEffects(fabric); T.__effectsInstalled = true; }
    var el = document.createElement('canvas');
    el.width = b.canvas.w; el.height = b.canvas.h;
    this.design = new fabric.StaticCanvas(el, { width: b.canvas.w, height: b.canvas.h, renderOnAddRemove: false, enableRetinaScaling: false });
    var scene = JSON.parse(JSON.stringify(b.scene));
    return Promise.resolve(this.design.loadFromJSON(scene)).then(function () {
      return T.scene.prepare(fabric, self.design, scene, b.tcdProps || [], {
        workingW: b.canvas.w, workingH: b.canvas.h, margins: b.margins, autoCenterVertical: b.autoCenterVertical !== false,
      });
    }).then(function (prep) {
      self.varState = prep.varState;
      self.imgState = prep.imgState;
    });
  };
  // Lay the values out on the design canvas (the renderer's own maths).
  Renderer.prototype.apply = function (values, fills) {
    var T = window.TcdRender, b = this.bundle;
    T.varLayout.apply(window.fabric, this.design, this.varState,
      { 1: values['1'] || '', 2: values['2'] || '', 3: values['3'] || '', 4: values['4'] || '' },
      { margins: b.margins, autoCenterVertical: b.autoCenterVertical !== false });
    if (this.imgState && T.varImages && T.varImages.reset) T.varImages.reset(window.fabric, this.design, this.imgState);

    // The chosen colour, AFTER varLayout — which has just reset to the
    // authored scene, and is exactly why this needs no restore of its own.
    // Same contract as the portal's renderer: an object naming tcdFillRole
    // wears the role's colour in `fill` and its texture in tcdFillImage, and
    // several objects may name the same role.
    if (fills && Object.keys(fills).length) {
      var walk = function (list) {
        for (var i = 0; i < list.length; i++) {
          var o = list[i];
          if (!o) continue;
          var role = parseInt(o.tcdFillRole, 10);
          var f = (role >= 1 && role <= 4) ? fills[role] : null;
          if (f) {
            var set = { tcdFillImage: f.image || '', tcdFillImageName: f.image ? (f.name || '') : '' };
            if (f.hex) set.fill = f.hex;
            try { o.set(set); } catch (e) { for (var k in set) o[k] = set[k]; }
            try { o.dirty = true; delete o.__tcdFillRaster; } catch (e) {}
          }
          if (o._objects && o._objects.length) walk(o._objects);
        }
      };
      walk(this.design.getObjects());
      // A texture has to decode before it draws, or the first paint shows the
      // colour underneath and only corrects itself on the next keystroke.
      if (T.preloadTextFills) {
        var self = this;
        T.preloadTextFills(this.design).then(function () { self.design.renderAll(); }, function () {});
      }
    }
    this.design.renderAll();
  };
  // Design → mug photo → frame. The photo is fitted inside a frame of the
  // requested aspect (the gallery's, or the photo's own); the design fits
  // 'contain' inside the overlay box (× design_scale), centred, bent and
  // alpha-blended — the renderer's geometry.
  Renderer.prototype.compositeInto = function (cv, view, cssW, dpr, frameAspect, treat) {
    var ctx = cv.getContext('2d');
    var src = this.design.getElement ? this.design.getElement() : this.design.lowerCanvasEl;
    var bg = this.cfg.background || '#ffffff';
    if (!view) {
      var side = Math.round(cssW * dpr);
      cv.width = side; cv.height = side; cv.style.width = cssW + 'px'; cv.style.height = cssW + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = bg; ctx.fillRect(0, 0, side, side);
      ctx.drawImage(src, 0, 0, src.width, src.height, 0, 0, side, side);
      return;
    }
    var img = view.img, m = view.meta;
    var natW = img.naturalWidth || img.width, natH = img.naturalHeight || img.height;
    var cssH = Math.round(cssW * (frameAspect || (natH / natW)));
    cv.width = Math.round(cssW * dpr); cv.height = Math.round(cssH * dpr);
    cv.style.width = cssW + 'px'; cv.style.height = cssH + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // The mug photos are cut-outs with a transparent background; a gallery
    // frame is a JPEG, so paint the page's white first or the cut-out sits
    // on black.
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, cssW, cssH);
    var ps = Math.min(cssW / natW, cssH / natH);
    var pw = natW * ps, ph = natH * ps, px = (cssW - pw) / 2, py = (cssH - ph) / 2;
    ctx.drawImage(img, px, py, pw, ph);
    // Overlay geometry was authored against the original photo pixels; if
    // the CDN copy differs in size, scale it along.
    var geomScale = (m.w && natW && m.w !== natW) ? natW / m.w : 1;
    var ox = px + m.overlay.x * geomScale * ps, oy = py + m.overlay.y * geomScale * ps;
    var ow = m.overlay.w * geomScale * ps, oh = m.overlay.h * geomScale * ps;
    var ds = Math.max(0.05, Math.min(2, Number(m.scale) || 1));
    var fit = Math.min(ow / src.width, oh / src.height) * ds;
    var dw = src.width * fit, dh = src.height * fit;
    var dx = ox + (ow - dw) / 2, dy = oy + (oh - dh) / 2;
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, Number(m.blend) || 1));
    drawCylindrical(ctx, src, 0, 0, src.width, src.height, dx, dy, dw, dh, Number(m.amp) || 0);
    ctx.restore();
    drawTreat(ctx, treat, treatSpotFor(m, view), ps, px, py, geomScale, cssW, cssH);
  };
  // A finished frame for values + view as a canvas (apply, then composite).
  Renderer.prototype.frame = function (values, viewIndex, width, aspect, treat) {
    this.apply(values);
    var cv = document.createElement('canvas');
    this.compositeInto(cv, this.views[viewIndex] || null, width, 1, aspect, treat);
    return cv;
  };

  // ─────────────────────────────────────────────────────────────────────
  // Shopify's express buttons — Buy with Shop, Apple Pay, Google Pay, "More payment options".
  // ─────────────────────────────────────────────────────────────────────
  // Philip, 7 Oct 2026: "on personalised items that require input the user click other payments or buy with shop
  // buttons and the requirement fields seem to be bypassed". They are Shopify's own components: a tap on one goes
  // straight to a checkout, never through the form's submit — so never through the check that the name is filled in,
  // and never through our add, which is what puts the preview on the line (the basket, the order page, the emails and
  // the portal all show it). So on a personalised product every tap on one is ours, caught on the page in the CAPTURE
  // phase before Shopify's handler can hear it: with a word missing, the box asks for it exactly as Add to Basket
  // does; with everything filled in, the mug goes into the basket the way Add to Basket puts it there, and the shopper
  // goes on to the checkout — where Shop Pay, Apple Pay and the rest are all offered again.
  var EXPRESS = '.shopify-payment-button, [data-shopify="payment-button"], shopify-accelerated-checkout, shopify-buy-it-now-button, .shopify-payment-button__button, .shopify-payment-button__more-options';
  var PERSONALISERS = [];

  // ─────────────────────────────────────────────────────────────────────
  // The shop's theme family (window.Towsons, from the portal's loader —
  // snippets/towsons-loader.liquid). Warehouse (WeDoMugs) is the default: no
  // loader, or a family not named here, behaves exactly as before. Horizon
  // (Philip, 10 Oct 2026: "start porting the personaliser to Horizon"):
  //   - the block sits in the product details, OUTSIDE the product form, so
  //     every property input is tied to the form by its form= attribute;
  //   - Horizon adds to the basket itself (product-form-component: the whole
  //     form, files included, then its own drawer and counts), so the preview
  //     is put INTO the form as a file and Horizon's submit carries it;
  //   - a colour change is Shopify's standard 'shopify:product:select', and
  //     Horizon redraws the gallery, not this block — the block follows it.
  // ─────────────────────────────────────────────────────────────────────
  function themeFamily() {
    var t = window.Towsons;
    var f = t && t.family ? String(t.family).toLowerCase() : '';
    return f === 'horizon' ? 'horizon' : 'warehouse';
  }
  var GALLERY = {
    warehouse: {
      media: '.product-gallery__carousel-item[data-media-id], .product__media-item, [data-media-id].product__media, .product-media-container',
      thumb: '.product-gallery__thumbnail[data-media-id], .thumbnail-list__item, .product__thumbnail',
    },
    // Horizon's gallery: the slideshow's slides and the grid's tiles both carry
    // .product-media-container (a grid page draws both, one hidden per screen
    // size — visibleMedia keeps the set showing); its thumbnails are buttons.
    horizon: {
      media: 'media-gallery .product-media-container',
      thumb: 'media-gallery .slideshow-controls__thumbnail',
    },
  };
  // A variant id however a theme gives it: 123, "123" or "gid://shopify/ProductVariant/123".
  function variantNumber(id) {
    var m = /(\d+)\s*$/.exec(String(id == null ? '' : id));
    return m ? m[1] : '';
  }
  // The express element a tap landed on, through any shadow root its button lives in; or null.
  function expressTarget(ev) {
    var path = ev && typeof ev.composedPath === 'function' ? ev.composedPath() : [];
    if (!path.length && ev) { for (var n = ev.target; n; n = n.parentNode || n.host) path.push(n); }
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (el && el.nodeType === 1 && typeof el.matches === 'function' && el.matches(EXPRESS)) return el;
    }
    return null;
  }
  // One tap on an express button, on a page with a personaliser (`p`: the one in the page now — the theme draws a
  // new one on each colour). Its first touch is only held back; the click decides. → what was done
  function expressGuard(ev, p) {
    var form = p && p.form;
    if (!form) return 'none';
    ev.stopImmediatePropagation();
    if (ev.type === 'pointerdown' || ev.type === 'mousedown' || ev.type === 'touchstart') return 'held';
    if (ev.type === 'keydown' && ev.key !== 'Enter' && ev.key !== ' ') return 'held';
    ev.preventDefault();
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      if (typeof form.reportValidity === 'function') form.reportValidity();
      return 'asked';
    }
    p._thenCheckout = true;
    p.onAdd(ev, 'express', form.querySelector('.product-form__add-button, button[type="submit"], input[type="submit"], [data-action="add-to-cart"]'));
    return 'added';
  }
  // A button Shopify draws in an iframe (PayPal's, for some shoppers) hears no tap of ours, so the express buttons wear a
  // clear shield: the pointer lands on it, inside the express box, and the guard has the tap whatever is drawn under
  // it. Put back if Shopify redraws the box. Keyboard taps reach the guard through the buttons themselves.
  function shieldExpress(p) {
    var box = p && p.form ? p.form.querySelector('.shopify-payment-button, [data-shopify="payment-button"]') : null;
    if (!box || box.__towsonsShield) return null;
    box.__towsonsShield = true;
    var pos = '';
    try { pos = window.getComputedStyle(box).position; } catch (e) { pos = ''; }
    if (!pos || pos === 'static') box.style.position = 'relative';
    var put = function () {
      for (var i = 0; i < box.children.length; i++) if (box.children[i].className === 'towsons-express-shield') return;
      var b = document.createElement('span');
      b.className = 'towsons-express-shield';
      b.setAttribute('aria-hidden', 'true');
      b.style.cssText = 'position:absolute;top:0;right:0;bottom:0;left:0;z-index:10;cursor:pointer;background:transparent';
      box.appendChild(b);
    };
    put();
    try { new MutationObserver(put).observe(box, { childList: true }); } catch (e) { /* the guard still has same-page taps */ }
    return box;
  }
  function wireExpress() {
    if (window.__towsonsExpressGuard) return;
    window.__towsonsExpressGuard = true;
    ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'click'].forEach(function (type) {
      document.addEventListener(type, function (ev) {
        var el = expressTarget(ev);
        if (!el) return;
        PERSONALISERS = PERSONALISERS.filter(function (x) { return x.root && x.root.isConnected; });
        var p = PERSONALISERS[PERSONALISERS.length - 1];
        if (!p || !p.form || !p.form.contains(el)) return;   // not this product's form: not ours
        expressGuard(ev, p);
      }, { capture: true, passive: false });
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Product page.
  // ─────────────────────────────────────────────────────────────────────
  function Personaliser(root) {
    this.root = root;
    this.cfg = parseJsonAttr(root, 'data-config') || {};
    // The colour the shopper is looking at. The theme re-renders this block on
    // a variant change, so it is current by construction.
    this.variantId = root.getAttribute('data-variant-id') || null;
    this.scripts = { fabric: root.getAttribute('data-fabric-src'), tcd: root.getAttribute('data-tcd-src') };
    // The mug photos arrive with the renderer; until then there are none.
    // The theme fires variant:changed 220ms before a re-rendered block has
    // its renderer, and the gallery reclaim read `.length` of nothing.
    this.views = [];
    this.inputs = Array.prototype.slice.call(root.querySelectorAll('input[data-slot]'));
    bindFieldRules(this.inputs, this.cfg);
    this.hidden = root.querySelector('[data-towsons-hidden]');
    this.canvas = root.querySelector('[data-canvas]');
    this.status = root.querySelector('[data-status]');
    this.viewsEl = root.querySelector('[data-views]');
    this.previewEl = root.querySelector('[data-preview]');
    this.editingEl = root.querySelector('[data-editing]');
    this.takeOverGallery = root.getAttribute('data-take-over-gallery') !== 'false';
    this.family = themeFamily();
    var gallery = GALLERY[this.family];
    this.gallerySelectors = {
      media: root.getAttribute('data-gallery-media') || gallery.media,
      thumb: root.getAttribute('data-gallery-thumb') || gallery.thumb,
      // Themes hide the media of the colours not chosen; painting into one of
      // those would leave the shopper's own colour showing the stock photo.
      // ONLY the theme's variant filter. A carousel parks its other slides
      // with aria-hidden and a --hidden class while they wait their turn, and
      // skipping those left every view but the first showing the stock photo.
      skip: root.getAttribute('data-gallery-skip') || '.is-filtered',
    };
    this.galleryTargets = null;
    this.viewIndex = 0;
    this.ready = false;
    this.pending = false;
    this.editKey = null;
    this.productKey = root.getAttribute('data-product-id') || '';
    this.treat = null;
    this.treatSeq = 0;
    this.wireForm();
    PERSONALISERS.push(this);
    wireExpress();
    shieldExpress(this);
    this.restoreTyped();
    this.wireInputs();
    this.writeHidden();
    this.startEditMode();
    var self = this;
    var start = function () { self.boot(); };
    // Boot when the block comes near the viewport or the shopper touches a
    // field — and after a short idle regardless, so the preview is warm by the
    // time they scroll to it (a personalised product page is what these
    // scripts are for).
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); start(); }
      }, { rootMargin: '300px' });
      io.observe(root);
    }
    this.inputs.forEach(function (i) {
      ['focus', 'input', 'pointerdown'].forEach(function (ev) { i.addEventListener(ev, start, { once: true }); });
    });
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2500 }); else setTimeout(start, 2500);
    this.prefillFromUrl();
  }

  // Arrived from a search that found nothing, with their words
  // (?personalise=Kevin, towsons-discover): the words go in the first box as
  // if typed — an input event, so the box's rules, the counter, the order's
  // hidden values and the live preview all follow the ordinary path. Never
  // over words already there (kept across a colour change), never in a
  // basket edit.
  Personaliser.prototype.prefillFromUrl = function () {
    if (this.editKey || this._editRequested) return;
    var first = this.inputs.filter(function (i) { return i.getAttribute('data-slot') === '1'; })[0];
    if (!first || first.value) return;
    var words = prefillFrom(window.location.href, this.cfg);
    if (!words) return;
    first.value = words;
    try { first.dispatchEvent(new Event('input', { bubbles: true })); } catch (e) { /* an old browser: the words are in the box */ }
  };

  // Attach the inputs to the add-to-cart form even when the block is mounted
  // outside it (a custom-liquid block, say). Inside the form this is a no-op.
  // The submit hook does two things: cache the frame the basket will show,
  // and in edit mode drop the line being replaced before the theme adds the
  // new one.
  // The product's own add-to-basket form for a block outside it. Not simply the
  // page's first: Horizon's price block draws a form of its own before it (the
  // instalments line's, class payment-terms, no button) — properties tied to
  // that one never reach the basket. The add form in the block's own section,
  // the one with a button, else the first on the page as before.
  function productForm(root) {
    var own = root.closest('form');
    if (own) return own;
    function pick(scope) {
      var all = Array.prototype.slice.call(scope.querySelectorAll('form[action*="/cart/add"]'))
        .filter(function (f) { return !f.classList.contains('payment-terms') && f.querySelector('[type="submit"]'); });
      return all.filter(function (f) { return f.getAttribute('data-type') === 'add-to-cart-form'; })[0] || all[0] || null;
    }
    var section = root.closest('.shopify-section');
    return (section && pick(section)) || pick(document) || document.querySelector('form[action*="/cart/add"]');
  }
  // The form's id as written on it. Never form.id: a product form holds an
  // input NAMED "id" (the variant), and the browser hands that input back for
  // form.id — so form= said "[object HTMLInputElement]" and tied nothing.
  function formIdOf(form) {
    var id = form.getAttribute('id');
    if (!id) { id = 'towsons-product-form-' + Math.random().toString(36).slice(2, 8); form.setAttribute('id', id); }
    return id;
  }
  Personaliser.prototype.wireForm = function () {
    var self = this;
    var form = productForm(this.root);
    evictOptionsApp(form);
    this.form = form;
    if (!form) return;
    var fid = formIdOf(form);
    this.formId = fid;
    var inside = form.contains(this.root);
    // Every property the block carries — the boxes, the chosen colours, the
    // order's hidden values — tied to the form when the block is outside it.
    var all = Array.prototype.slice.call(this.root.querySelectorAll('input[name], select[name], textarea[name]'))
      .filter(function (i) { return String(i.getAttribute('name')).indexOf('properties[') === 0; });
    all.forEach(function (i) { if (!inside) i.setAttribute('form', fid); });
    this.formInside = inside;
    // Themes add to basket two ways: a real form submit, or (Warehouse's
    // <buy-button>) a click listener on the button that stops propagation and
    // calls the cart API itself. Both are caught here in the CAPTURE phase on
    // the form, which runs before either.
    // Horizon hears every submit at the DOCUMENT, in the capture phase
    // (assets/component.js: on:submit is delegated there), so a listener on
    // the form hears it only after Horizon has sent the line. The window is
    // the one place before it. Its add button is a real submit, so no click
    // hook either — that would take the add away from Horizon's own basket.
    // Every add goes to the block on the page NOW (current()), whichever
    // personaliser's listener hears it first.
    var add = function (ev, kind, btn) {
      var me = self.current();
      if (me) me.onAdd(ev, kind, btn);
    };
    if (this.family === 'horizon') {
      window.addEventListener('submit', function (ev) {
        if (ev.target !== self.form) return;
        add(ev, 'submit');
      }, true);
      return;
    }
    form.addEventListener('submit', function (ev) { add(ev, 'submit'); }, true);
    form.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest('button[type="submit"], input[type="submit"], .product-form__add-button, [data-action="add-to-cart"]') : null;
      if (btn && form.contains(btn)) add(ev, 'click', btn);
    }, true);
  };

  // THE BLOCK ON THE PAGE NOW (Philip, 23 Oct 2026, WDM-4530: the order's
  // picture was a pink mug saying "Kevin", its line Cambridge Blue 'KEVIN!'
  // with a Biscoff). A theme may draw this block again while the product form
  // stays — Warehouse does on every colour change — and the listeners put on
  // the form stay with it. The first personaliser's heard the add first and
  // stopped the rest, so the picture was drawn from ITS boxes, colour and
  // treat: the page as it opened (here, from the Kevin mug's "More colours",
  // on the product's first colour, Pink), while the form sent the new block's
  // words, colour and treat. So an add is always handed to the personaliser of
  // the block on the page now, for the same form; a block drawn a moment ago
  // and not started yet is started here. Found through the page: the block is
  // where the personaliser lives (root.__towsonsPersonaliser). Null when the
  // form has none — the theme's own add then goes, the words without a picture.
  Personaliser.prototype.current = function () {
    if (this.root.isConnected) return this;
    var form = this.form;
    if (!form) return null;
    var find = function () {
      var found = null;
      Array.prototype.forEach.call(document.querySelectorAll('[data-towsons-personaliser]'), function (root) {
        var p = root.__towsonsPersonaliser;
        if (p && p.form === form) found = p;
      });
      return found;
    };
    var me = find();
    if (!me) { init(); me = find(); }
    return me;
  };

  // Horizon adds to the basket itself, from the whole form: the preview goes
  // INTO the form as a file (a hidden file input, filled through a
  // DataTransfer), and the submit is handed back to Horizon — which sends the
  // line multipart, with every property, then opens its drawer and updates its
  // counts as it always does. True when the add was handed over; false when
  // this browser cannot fill a file input (then the add is ours, as on
  // Warehouse).
  Personaliser.prototype.canHandOver = function () {
    return this.family === 'horizon' && typeof DataTransfer === 'function' && typeof File === 'function'
      && !!this.form && typeof this.form.requestSubmit === 'function' && !this._thenCheckout;
  };
  Personaliser.prototype.previewInput = function () {
    var form = this.form;
    var el = this._previewInput;
    if (!el || !el.isConnected) {
      el = document.createElement('input');
      el.type = 'file';
      el.name = 'properties[_preview]';
      el.hidden = true;
      el.setAttribute('data-towsons-preview-file', '');
      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-hidden', 'true');
      if (this.formInside) form.appendChild(el); else { el.setAttribute('form', formIdOf(form)); this.root.appendChild(el); }
      this._previewInput = el;
    }
    return el;
  };
  Personaliser.prototype.handOver = function (ev, submitter) {
    var form = this.form, self = this;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    this._adding = true;
    if (submitter) { submitter.setAttribute('aria-busy', 'true'); submitter.classList.add('towsons-personaliser__busy'); }
    this.cacheFrames();
    var removeOld = this.editKey ? fetch(cartUrl('change'), {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: this.editKey, quantity: 0 }),
    }).catch(function () {}) : Promise.resolve();
    removeOld.then(function () { return self.previewBlob(); }).then(function (blob) {
      var input = self.previewInput();
      // No picture (the preview not drawn yet): the empty file box stays out of
      // the form altogether, or the line would carry an empty "_preview".
      input.disabled = !blob;
      if (!blob) return;
      try {
        var dt = new DataTransfer();
        dt.items.add(new File([blob], 'preview.jpg', { type: 'image/jpeg' }));
        input.files = dt.files;
      } catch (e) { input.disabled = true; /* no preview on the line — the words still go */ }
    }).catch(function () { /* the words go without the picture */ }).then(function () {
      self._adding = false;
      if (submitter) { submitter.removeAttribute('aria-busy'); submitter.classList.remove('towsons-personaliser__busy'); }
      self._passing = true;
      try { form.requestSubmit(submitter && submitter.form === form ? submitter : undefined); } finally { self._passing = false; }
      if (self.editKey !== null || self._editButton) { self.editKey = null; self.finishEditMode(); }
    });
  };

  // The add is ours on a personalised product: the theme would send the form
  // as JSON, and the preview has to travel as a FILE property (Shopify stores
  // line-item file uploads on its CDN and keeps the URL on the order, which
  // is what lets the basket, the account's order page, the notification
  // emails and the portal all show the mug the shopper saw). Afterwards the
  // theme's own product:added event is dispatched with the detail it expects,
  // so the drawer opens exactly as it would have.
  Personaliser.prototype.onAdd = function (ev, kind, btn) {
    // The submit handed back to Horizon, with the preview in the form: Horizon's.
    if (this._passing) return;
    if (this._adding) { ev.preventDefault(); ev.stopImmediatePropagation(); return; }
    var form = this.form, self = this;
    if (!form || typeof FormData === 'undefined') return; // let the theme do it
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      if (typeof form.reportValidity === 'function') { ev.preventDefault(); ev.stopImmediatePropagation(); form.reportValidity(); }
      return;
    }
    if (this.canHandOver()) return this.handOver(ev, btn || ev.submitter || form.querySelector('[type="submit"]'));
    ev.preventDefault();
    ev.stopImmediatePropagation();
    this._adding = true;
    var submitter = btn || ev.submitter || form.querySelector('[type="submit"]');
    if (submitter) submitter.setAttribute('disabled', 'disabled');
    // THE TWO SECONDS HAVE TO LOOK LIKE SOMETHING. The preview is drawn and
    // uploaded with the line, which takes a moment, and the button sat there
    // saying "Add to Basket" as though the tap had missed.
    this.adding(submitter, true);
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    this.cacheFrames();
    var removeOld = this.editKey ? fetch(cartUrl('change'), {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: this.editKey, quantity: 0 }),
    }).catch(function () {}) : Promise.resolve();
    removeOld.then(function () { return self.previewBlob(); }).then(function (blob) {
      var fd = new FormData(form);
      if (blob) fd.append('properties[_preview]', blob, 'preview.jpg');
      return fetch(cartUrl('add'), { method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
    }).then(function (response) {
      var qtyEl = form.querySelector('[name="quantity"]');
      var host = form.querySelector('buy-button') || submitter || form;
      // The theme's "Added!" swap reads the button's own label to put back
      // afterwards, so ours has to be gone before it hears about this.
      self.adding(submitter, false);
      // An express button's tap: the line is in, with its preview — on to the checkout, where the express ways are.
      if (self._thenCheckout && response && response.ok) { window.location.href = (window.routes && window.routes.checkoutUrl) || '/checkout'; return; }
      host.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { button: submitter, variant: null, quantity: parseInt(qtyEl ? qtyEl.value : '1', 10) || 1, productAdded: !!(response && response.ok) } }));
      if (response && response.ok) {
        // A Horizon page that could not hand the add over (an old browser): its drawer knows nothing of the
        // line, so the basket page shows it.
        if (self.family === 'horizon') { window.location.href = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart'; return; }
        if ((window.theme && window.theme.pageType === 'cart') || (window.theme && window.theme.cartType === 'page')) { window.location.href = (window.routes && window.routes.cartUrl) || '/cart'; return; }
        if (window.theme && window.theme.cartType === 'drawer') document.dispatchEvent(new CustomEvent('modal:close'));
      } else {
        return response.json().then(function (j) { alert(j && (j.description || j.message) || 'Could not add to basket'); }, function () { alert('Could not add to basket'); });
      }
    }).catch(function (e) {
      if (window.console && console.warn) console.warn('[towsons-personaliser] add failed:', e && e.message ? e.message : e);
      alert('Could not add to basket. Please try again.');
    }).then(function () {
      self._adding = false;
      self._thenCheckout = false;
      self.adding(submitter, false);
      if (submitter) submitter.removeAttribute('disabled');
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      if (self.editKey !== null || self._editButton) { self.editKey = null; self.finishEditMode(); }
    });
  };

  // "Adding" with a spinner while the line is on its way, then the button's
  // own label back exactly as it was.
  Personaliser.prototype.adding = function (btn, on) {
    if (!btn) return;
    if (on) {
      if (this._addingWas) return;
      this._addingWas = { el: btn, html: btn.innerHTML };
      btn.classList.add('towsons-personaliser__adding');
      var spin = document.createElement('span');
      spin.className = 'towsons-personaliser__spinner';
      spin.setAttribute('aria-hidden', 'true');
      var word = document.createElement('span');
      word.textContent = this.root.getAttribute('data-adding-label') || 'Adding';
      btn.textContent = '';
      btn.appendChild(spin);
      btn.appendChild(word);
      return;
    }
    var was = this._addingWas;
    if (!was) return;
    this._addingWas = null;
    was.el.classList.remove('towsons-personaliser__adding');
    was.el.innerHTML = was.html;
  };

  // The frame the order keeps: the first mug view at 600px, as a JPEG blob.
  Personaliser.prototype.previewBlob = function () {
    if (!this.renderer || !this.renderer.ready) return Promise.resolve(null);
    try {
      var aspect = (this.galleryTargets && this.galleryTargets[0]) ? this.galleryTargets[0].aspect : null;
      return canvasToBlob(this.renderer.frame(this.artworkValues(), 0, 600, aspect, this.treatAtRest()));
    } catch (e) { return Promise.resolve(null); }
  };

  Personaliser.prototype.finishEditMode = function () {
    if (this._editFinished) return; this._editFinished = true;
    this.editKey = null;
    if (this.editingEl) { this.editingEl.setAttribute('hidden', ''); }
    if (this._editButton) {
      this._editButton.el.textContent = this._editButton.was;
      if (this._editButton.wasAria != null) this._editButton.btn.setAttribute('aria-label', this._editButton.wasAria); else this._editButton.btn.removeAttribute('aria-label');
      this._editButton = null;
    }
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.has('towsons_edit')) { u.searchParams.delete('towsons_edit'); window.history.replaceState(null, '', u.toString()); }
    } catch (e) { /* leave the URL */ }
  };

  // What the basket shows for this line, written before the add so the
  // drawer can paint it without loading the renderer again.
  Personaliser.prototype.cacheFrames = function () {
    if (!this.renderer || !this.renderer.ready || !this.renderer.fontsOk) return;
    var values = this.artworkValues(), cfg = this.cfg;
    for (var v = 0; v < Math.max(1, this.renderer.views.length); v++) {
      try {
        var aspect = (this.galleryTargets && this.galleryTargets[v]) ? this.galleryTargets[v].aspect : null;
        var cv = this.renderer.frame(values, v, 360, aspect, this.treatAtRest());
        cacheSet(previewCacheKey(cfg.template_id, cfg.change_id, values, v, blankFor(cfg, this.variantId), this.treatName()), cv.toDataURL('image/jpeg', 0.85));
      } catch (e) { /* cache is a convenience */ }
    }
  };

  Personaliser.prototype.wireInputs = function () {
    var self = this;
    var render = debounce(function () { self.render(); }, 60);
    this.inputs.forEach(function (i) {
      var counter = self.root.querySelector('[data-count-for="' + i.getAttribute('data-slot') + '"]');
      var tick = function () {
        if (counter) counter.textContent = i.value.length + '/' + (i.maxLength > 0 ? i.maxLength : '');
        self.writeHidden();
        self.rememberTyped();
        // Their own words: from here the gallery is their mug (goLive).
        if (String(i.value || '').trim()) self.goLive();
        render();
      };
      i.addEventListener('input', tick);
      i.addEventListener('change', tick);
      tick();
    });
  };

  // What the shopper typed, kept for this page only. The theme redraws this
  // block on a variant change and Liquid hands back empty fields, so without
  // this a colour change wipes the name.
  var TYPED = window.__towsonsPersonaliserTyped || (window.__towsonsPersonaliserTyped = {});
  Personaliser.prototype.rememberTyped = function () {
    if (this.productKey) TYPED[this.productKey] = this.values();
  };
  Personaliser.prototype.restoreTyped = function () {
    var kept = this.productKey && TYPED[this.productKey];
    if (!kept) return;
    this.inputs.forEach(function (i) {
      var v = kept[i.getAttribute('data-slot')];
      if (v) i.value = v;
    });
  };

  // ── The field rules, as the bulk template set them ──────────────────────
  //
  // Three things live on a slot besides its label: a prefix the artwork always
  // carries, a case the artwork sets, and a default it shows before anybody
  // types. All three change what is DRAWN, none of them changes what the
  // customer typed — the order carries the typed value and the portal applies
  // exactly these rules again when it builds the design to print. Applying
  // them here as well would make the preview and the print disagree the moment
  // one side changed.
  function applyCase(str, mode) {
    var s = str == null ? '' : String(str);
    if (!s) return s;
    if (mode === 'upper') return s.toUpperCase();
    if (mode === 'lower') return s.toLowerCase();
    if (mode === 'first') return s.charAt(0).toUpperCase() + s.slice(1);
    if (mode === 'words') {
      return s.toLowerCase().replace(/(^|[\s'\-])([a-z\u00E0-\u024F])/g, function (m, sep, c) {
        return sep + c.toUpperCase();
      });
    }
    return s;
  }

  // Letters the box's font can't draw become their plain letter (Philip, 22
  // Oct 2026: on the Warning mug "José" drew "Jos" + a fallback "é"). `missing`
  // is the slot's `fold`, worked out from the font by the portal
  // (lib/designs/fontLetters), so a font that has é keeps it. Same as
  // personalisationSpec.foldLetters — pinned equal by test.
  var FOLD_EXTRA = { 'ß': 'ss', 'ẞ': 'SS', 'Æ': 'AE', 'æ': 'ae', 'Œ': 'OE', 'œ': 'oe', 'Ø': 'O', 'ø': 'o', 'Ł': 'L', 'ł': 'l', 'Đ': 'D', 'đ': 'd', 'Ð': 'D', 'ð': 'd', 'Þ': 'TH', 'þ': 'th', 'ı': 'i', 'Ħ': 'H', 'ħ': 'h', 'Ŧ': 'T', 'ŧ': 't', 'ĸ': 'k', 'Ŀ': 'L', 'ŀ': 'l', 'ŉ': 'n', 'Ŋ': 'N', 'ŋ': 'n', 'Ĳ': 'IJ', 'ĳ': 'ij', 'ſ': 's' };
  function foldLetters(str, missing) {
    var s = str == null ? '' : String(str);
    if (!s || !missing) return s;
    var out = '';
    for (var i = 0; i < s.length; i++) {
      var c = s.charAt(i);
      if (missing.indexOf(c) === -1) { out += c; continue; }
      if (FOLD_EXTRA[c]) { out += FOLD_EXTRA[c]; continue; }
      var plain = c.normalize ? c.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
      out += /^[A-Za-z]+$/.test(plain) ? plain : '';
    }
    return out;
  }

  // What the artwork carries for one slot: their words, cased, behind the
  // prefix — or the slot's default when they have typed nothing. Plain
  // letters after the case: a font can have "é" and lack "É".
  function artworkValue(raw, slot) {
    var typed = String(raw == null ? '' : raw).trim();
    var body = typed || String((slot && slot.default_value) || '').trim();
    if (!body) return '';
    return String((slot && slot.prefix) || '') + foldLetters(applyCase(body, slot && slot.letter_case), slot && slot.fold);
  }

  function slotsByN(cfg) {
    var by = {};
    ((cfg && cfg.slots) || []).forEach(function (s) { by[String(s.n)] = s; });
    return by;
  }

  // SOMEBODY'S WORDS, BY ONE PRODUCT'S RULES — for a page that is not the
  // product's own (Philip, 29 Sep 2026: "If they search for Kevin and nothing
  // shows then show a selection of personalised products with the name
  // rendered"). The words go in the first box as the box would take them as
  // typed (its allowed characters, its case) and must fit its length; every
  // other box shows its default, as before anybody types. → { typed, art } —
  // what the box would hold, what the artwork carries — or null when the
  // words are nothing this product can print.
  function wordsFor(cfg, text) {
    var by = slotsByN(cfg);
    var first = by['1'];
    if (!first) return null;
    var body = String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
    // A box that takes no spaces would make "kevin smith" KevinSmith; from a
    // search, the first name reads better than the two run together.
    if (first.allowed_pattern && body.indexOf(' ') >= 0) {
      try { if (new RegExp('[^' + first.allowed_pattern + ']').test(' ')) body = body.split(' ')[0]; }
      catch (e) { /* a pattern this browser cannot read: leave the words */ }
    }
    if (first.allowed_pattern) {
      try { body = body.replace(new RegExp('[^' + first.allowed_pattern + ']', 'g'), '').replace(/\s+/g, ' ').trim(); }
      catch (e) { /* a pattern this browser cannot read: the box would not filter either */ }
    }
    if (first.letter_case) body = applyCase(body, first.letter_case);
    if (first.fold) body = foldLetters(body, first.fold);
    if (!body) return null;
    var max = Number(first.max) || 0;
    if (max && body.length > max) return null;
    var art = {};
    Object.keys(by).forEach(function (n) { art[n] = artworkValue(n === '1' ? body : '', by[n]); });
    return { typed: body, art: art };
  }

  // One mug with those words on it, drawn by the product page's own renderer
  // on the blank of the variant given. → Promise<canvas|null>.
  function preview(cfg, scripts, text, opts) {
    var w = cfg && cfg.bundle_url ? wordsFor(cfg, text) : null;
    if (!w) return Promise.resolve(null);
    var o = opts || {};
    return Renderer.get(cfg, scripts, o.variantId || null).then(function (r) {
      r.apply(w.art);
      var cv = document.createElement('canvas');
      r.compositeInto(cv, r.views[0] || null, o.width || 320, Math.min(window.devicePixelRatio || 1, 2), o.aspect || null, null);
      return cv;
    });
  }

  // ?personalise=Kevin — the words such a page sends people here with, as
  // this product's first box would take them, or null.
  function prefillFrom(href, cfg) {
    var raw = null;
    try { raw = new URL(href).searchParams.get('personalise'); } catch (e) { raw = null; }
    if (!raw) return null;
    var w = wordsFor(cfg, raw);
    return w ? w.typed : null;
  }

  // Keep a box to what it accepts, as they type. The portal applies the same
  // rule to the order, so this is about not letting somebody type a character
  // that would silently vanish from their mug.
  // attr: the product page marks its inputs data-slot, the basket editor
  // data-line-slot. Passing it in rather than guessing — reading the wrong one
  // finds no slot and binds nothing, which looks exactly like working.
  function bindFieldRules(inputs, cfg, attr) {
    var by = slotsByN(cfg);
    inputs.forEach(function (i) {
      var slot = by[i.getAttribute(attr || 'data-slot')];
      if (!slot) return;
      var body = i.getAttribute('data-allowed') || slot.allowed_pattern || '';
      if (!body && !slot.letter_case && !slot.fold) return;
      var re = body ? new RegExp('[^' + body + ']', 'g') : null;
      i.addEventListener('input', function () {
        var before = i.value;
        var after = before;
        if (re) after = after.replace(re, '');
        if (slot.letter_case) after = applyCase(after, slot.letter_case);
        // é → e as they type, where the design's font has no é: they see the
        // letter the mug will carry, not one that vanishes or prints wrong.
        if (slot.fold) after = foldLetters(after, slot.fold);
        if (after === before) return;
        // Keep the caret where it was rather than throwing it to the end,
        // which is what makes a filtered field unusable mid-word.
        var pos = i.selectionStart == null ? after.length : i.selectionStart - (before.length - after.length);
        i.value = after;
        try { i.setSelectionRange(Math.max(0, pos), Math.max(0, pos)); } catch (e) { /* not a text input */ }
      });
    });
  }

  // WHAT THEY TYPED. This is what the order carries and what the hidden
  // payload records, so the portal can apply the rules itself.
  Personaliser.prototype.values = function () {
    var v = {};
    this.inputs.forEach(function (i) { v[i.getAttribute('data-slot')] = i.value; });
    return v;
  };

  // WHICH FILL EACH ROLE IS ON. The hidden input beside the swatches holds the
  // key, because that is what travels on the line and what the order side
  // resolves by — reading the pressed button instead would make the preview
  // and the order two separate sources of truth.
  Personaliser.prototype.fillValues = function () {
    var cfg = this.cfg, out = {};
    ((cfg && cfg.fill_roles) || []).forEach(function (r) { by(r); });
    function by(r) {
      var el = document.querySelector('[data-fill-value="' + r.role + '"]');
      var key = (el && el.value) || r['default'];
      var f = (r.fills || []).filter(function (x) { return x.key === key; })[0]
        || (r.fills || []).filter(function (x) { return x.key === r['default']; })[0];
      if (f) out[r.role] = { fill_key: f.key, name: f.name, hex: f.hex, image: f.image || null };
    }
    return out;
  };

  // WHAT THE ARTWORK SHOWS. Everything that draws goes through here.
  Personaliser.prototype.artworkValues = function () {
    var by = slotsByN(this.cfg), v = this.values(), out = {};
    Object.keys(v).forEach(function (k) { out[k] = artworkValue(v[k], by[k]); });
    return out;
  };

  // What the order carries besides the visible properties: template + change
  // id + the values by slot, so the importer renders exactly this artwork.
  Personaliser.prototype.writeHidden = function () {
    if (!this.hidden) return;
    var v = this.values(), any = false, compact = {};
    Object.keys(v).forEach(function (k) { if (v[k] !== '') { compact[k] = v[k]; any = true; } });
    // The fills ride in the payload as well as in their own properties: the
    // properties are what a person reads on the order, the payload is what the
    // importer renders from, and a value in only one of them is a mug that
    // does not match its own line.
    var f = this.fillValues(), fills = {};
    Object.keys(f).forEach(function (n) { fills[n] = f[n].fill_key; });
    var hasFills = Object.keys(fills).length > 0;
    this.hidden.value = (any || hasFills)
      ? JSON.stringify(Object.assign({ t: this.cfg.template_id, c: this.cfg.change_id, v: compact },
        hasFills ? { f: fills } : {}))
      : '';
  };

  Personaliser.prototype.setStatus = function (msg) {
    if (!this.status) return;
    this.status.textContent = msg || '';
    if (msg) this.status.removeAttribute('hidden'); else this.status.setAttribute('hidden', '');
  };

  // Opened from a basket line's "Edit" link: ?towsons_edit=<line key>. The
  // line's values pre-fill the fields, the quantity follows, and the button
  // says what will happen.
  Personaliser.prototype.startEditMode = function () {
    var key = null;
    try { key = new URL(window.location.href).searchParams.get('towsons_edit'); } catch (e) { key = null; }
    if (!key) return;
    // Their words are on the way from the basket: no resting on the photos.
    this._editRequested = true;
    var self = this;
    fetch((window.routes && window.routes.cartUrl ? window.routes.cartUrl : '/cart') + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = (cart.items || []).filter(function (it) { return it.key === key; })[0];
        if (!line) return;
        var carried = null;
        try { carried = JSON.parse((line.properties || {})._towsons_personaliser || 'null'); } catch (e) { carried = null; }
        var values = (carried && carried.v) || {};
        self.inputs.forEach(function (i) {
          var slot = i.getAttribute('data-slot');
          var fromProps = null;
          var s = (self.cfg.slots || []).filter(function (x) { return String(x.n) === slot; })[0];
          if (s && line.properties && line.properties[s.property] != null) fromProps = line.properties[s.property];
          i.value = values[slot] != null ? values[slot] : (fromProps || '');
          i.dispatchEvent(new Event('input', { bubbles: true }));
        });
        var qty = self.form && self.form.querySelector('[name="quantity"]');
        if (qty && line.quantity) { qty.value = line.quantity; qty.dispatchEvent(new Event('change', { bubbles: true })); }
        // The treat comes back too — once its dropdown has initialised.
        var restoreTreat = function () {
          var roots = document.querySelectorAll('[data-towsons-treats]');
          var pending = false;
          Array.prototype.forEach.call(roots, function (r) {
            var hidden = r.querySelector('[data-treats-value]');
            var prop = hidden && (hidden.getAttribute('data-line-treat') || (hidden.name || '').replace(/^properties\[(.*)\]$/, '$1'));
            if (!prop || line.properties == null || line.properties[prop] == null) return;
            if (r.__towsonsTreats) r.__towsonsTreats.selectValue(line.properties[prop], true); else pending = true;
          });
          if (pending) setTimeout(restoreTreat, 150);
        };
        restoreTreat();
        self.editKey = key;
        self._editHandled = false;
        var btn = self.form && self.form.querySelector('[type="submit"]');
        if (btn) {
          var label = self.root.getAttribute('data-update-label') || 'Update basket';
          var span = btn.querySelector('span') || btn;
          self._editButton = { el: span, was: span.textContent, btn: btn, wasAria: btn.getAttribute('aria-label') };
          span.textContent = label;
          btn.setAttribute('aria-label', label);
        }
        if (self.editingEl) {
          var txt = self.editingEl.querySelector('[data-editing-text]') || self.editingEl;
          txt.textContent = self.root.getAttribute('data-editing-text') || 'Editing the item in your basket — change the text and update.';
          self.editingEl.removeAttribute('hidden');
          try { self.editingEl.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
        }
      }).catch(function () { /* not in the basket any more: plain product page */ });
  };

  // Pressing a swatch sets the hidden input the line carries, moves the
  // pressed state, and redraws. Delegated from the root so it survives the
  // theme re-rendering this block on a variant change.
  Personaliser.prototype.bindSwatches = function () {
    var self = this;
    if (this.root.__towsonsSwatches) return;
    this.root.__towsonsSwatches = true;
    this.root.addEventListener('click', function (ev) {
      var btn = ev.target.closest && ev.target.closest('[data-fill]');
      if (!btn || !self.root.contains(btn)) return;
      var group = btn.closest('[data-fill-role]');
      if (!group) return;
      ev.preventDefault();
      var role = group.getAttribute('data-fill-role');
      var hidden = group.querySelector('[data-fill-value="' + role + '"]');
      if (hidden) hidden.value = btn.getAttribute('data-fill') || '';
      Array.prototype.forEach.call(group.querySelectorAll('[data-fill]'), function (b) {
        var on = b === btn;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      self.writeHidden();
      self.goLive();
      self.render();
    });
  };

  // AT REST, THE PRODUCT'S OWN PHOTOS (Philip, 28 Sep 2026: "kinda make me
  // wonder why the example/shot file even needs rendered website side if we
  // make up the same shot files"). The portal renders the listing photos from
  // the very example the shop draws before anyone types — the two are kept one
  // value (lib/publish/personalisedSample) — so drawing that example over the
  // gallery on every page load showed nobody anything new, and cost a
  // "Generating your preview…" wait for the renderer, the fonts and the mug
  // photos before the first picture settled. The gallery now stays the photos
  // until the shopper makes the mug theirs: types, picks a colour for part of
  // the artwork, or chooses a treat (the photos show no treat). The renderer
  // still loads in the background, so that moment is instant, and whatever is
  // theirs on arrival — words kept across a colour change, a basket edit, a
  // treat already chosen — takes the gallery over straight away, as before.
  Personaliser.prototype.personalised = function () {
    var v = this.values();
    if (Object.keys(v).some(function (k) { return String(v[k] || '').trim() !== ''; })) return true;
    if (this.editKey || this._editRequested) return true;
    if (this.treatChoice()) return true;
    var roles = (this.cfg && this.cfg.fill_roles) || [];
    return roles.some(function (r) {
      var el = document.querySelector('[data-fill-value="' + r.role + '"]');
      return !!(el && el.value && el.value !== r['default']);
    });
  };

  // From the photos to their mug. Once only: clearing the words again shows
  // the example, which is what the photos showed anyway.
  Personaliser.prototype.goLive = function () {
    if (this.live) return;
    this.live = true;
    if (!this.takeOverGallery) return;
    // Not loaded yet: say so on the photo, and boot() takes over when it is.
    if (!this.ready) { if (this.booted) this.showGenerating(); return; }
    this.claimGallery();
    this.buildViewButtons();
    this.render();
    warmOtherBlanks(this.cfg, this.scripts, this.renderer.blankSku);
  };

  Personaliser.prototype.boot = function () {
    if (this.booted) return; this.booted = true;
    var self = this, cfg = this.cfg;
    // No bundle and no canvas means there is no preview to show, so the box
    // stays hidden rather than sitting there empty.
    if (!cfg.bundle_url || !this.canvas) { this.setStatus(''); return; }
    // "Generating your preview…" is for a wait. Switching to a colour already
    // warmed is not one, and flashing it makes an instant change look slow.
    // THE IN-CARD BOX IS THE FALLBACK, NOT THE FIRST THING TO SHOW.
    //
    // When the preview takes over the gallery — the normal case — the box is
    // hidden the moment claimGallery succeeds, but that only happens once the
    // bundle, the fonts and the mug photos are in. Until then a large empty
    // rectangle saying "Loading preview…" sat inside the choices card, pushing
    // the freebie question off the screen, while showGenerating was already
    // saying the same thing on the gallery image. Hidden up front, and given
    // back only if the takeover does not happen.
    //
    // It now ships hidden from the snippet too, because this script is
    // deferred: hiding it here still let the browser paint it first. So the
    // only thing left to do up front is the OTHER case — with no gallery to
    // take over, this box IS the preview and nothing else will show it.
    if (!this.takeOverGallery && this.previewEl) this.previewEl.removeAttribute('hidden');
    // At rest the gallery keeps the product's own photos (personalised()).
    // In-card mode has no photos to keep, so it is live from the start.
    if (!this.live) this.live = !this.takeOverGallery || this.personalised();
    if (this.live && !Renderer.has(cfg, this.variantId)) this.showGenerating();
    Renderer.get(cfg, this.scripts, this.variantId).then(function (renderer) {
      self.renderer = renderer;
      self.views = renderer.views;
      if (self.live) {
        self.claimGallery();
        self.buildViewButtons();
      }
      self.ready = true;
      self.bindSwatches();
      self.setStatus('');
      self.render();
      // A freebie already chosen (the theme redraws this block on a colour
      // change) is shown at rest, not popped again.
      self.setTreat(self.treatChoice(), false);
      // Now that they can see their mug, quietly fetch the others so the first
      // colour change does not stall on two photos. At rest a colour change
      // shows that colour's photos, so nothing needs fetching yet — goLive()
      // warms them when it happens.
      if (self.live) warmOtherBlanks(cfg, self.scripts, renderer.blankSku);
    }).catch(function (e) {
      self.setStatus('');
      self.hideGenerating();
      if (window.console && console.warn) console.warn('[towsons-personaliser] preview unavailable:', e && e.message ? e.message : e);
      // The box was hidden before the load began; there is nothing to put in
      // it now, so it stays hidden rather than coming back empty. In-card mode
      // was handed it in boot(), so that one has to be taken back.
      if (self.previewEl) self.previewEl.setAttribute('hidden', '');
      if (self.canvas) self.canvas.setAttribute('hidden', '');
      if (self.viewsEl) self.viewsEl.setAttribute('hidden', '');
    });
  };

  // The product's own images become the preview: view i is drawn into the
  // i-th gallery image (main and thumbnail), so the shopper sees the mug they
  // are personalising in the place they already look. The in-card canvas then
  // steps aside. Themes name their gallery differently, so the selectors come
  // from the wiring; when nothing matches, the in-card preview stays.
  Personaliser.prototype.visibleMedia = function () {
    var skip = this.gallerySelectors.skip;
    var all = Array.prototype.slice.call(document.querySelectorAll(this.gallerySelectors.media))
      .filter(function (el) { try { return !skip || !el.matches(skip); } catch (e) { return true; } });
    // A theme that draws the gallery twice — Horizon's grid for a computer and
    // its slideshow for a phone, one of them display:none — has its views
    // painted into the one showing. A parked carousel slide still has a box,
    // so it counts as showing; only a set the screen size hides is passed over.
    var shown = all.filter(function (el) { return el.getClientRects && el.getClientRects().length > 0; });
    return shown.length ? shown : all;
  };

  // A new variant shows a different set of the product's images, so the
  // preview has to be drawn into that set instead. The old blob URLs are left
  // on the images now hidden: picking that colour again claims and repaints
  // them, and a revoked URL would show as a broken image in between.
  // Another colour, on a theme that does not redraw this block (Horizon): the
  // mug it draws on follows, and so does the gallery the theme has just put
  // up. A colour already warmed is instant; another waits for its photos.
  Personaliser.prototype.switchVariant = function (id) {
    var self = this;
    id = variantNumber(id);
    if (!id || id === String(this.variantId || '')) { this.reclaimGallery(); return; }
    this.variantId = id;
    this.root.setAttribute('data-variant-id', id);
    if (!this.booted || !this.cfg.bundle_url) return;   // boot() reads the new one
    if (this.live && !Renderer.has(this.cfg, id)) this.showGenerating();
    Renderer.get(this.cfg, this.scripts, id).then(function (renderer) {
      if (String(self.variantId) !== id) return;      // another colour since
      self.renderer = renderer;
      self.views = renderer.views;
      self.ready = true;
      self.galleryTargets = null;
      if (self.live) { self.claimGallery(); self.buildViewButtons(); }
      self.render();
      self.setTreat(self.treatChoice(), false);
      if (self.live) warmOtherBlanks(self.cfg, self.scripts, renderer.blankSku);
    }, function (e) {
      if (window.console && console.warn) console.warn('[towsons-personaliser] colour change:', e && e.message ? e.message : e);
    });
  };

  Personaliser.prototype.reclaimGallery = function () {
    if (!this.takeOverGallery || !this.live) return;
    var before = this.galleryTargets ? this.galleryTargets.map(function (t) { return t.imgs[0]; }) : [];
    this.galleryTargets = null;
    this.claimGallery();
    var after = this.galleryTargets ? this.galleryTargets.map(function (t) { return t.imgs[0]; }) : [];
    var same = before.length === after.length && before.every(function (el, i) { return el === after[i]; });
    if (this.galleryTargets && !same) this.render();
  };

  Personaliser.prototype.claimGallery = function () {
    // Hidden up front in boot(), so every path out of here that leaves the
    // gallery unclaimed has to hand the in-card preview back — otherwise the
    // shopper gets no preview at all, which is worse than the box.
    var self = this;
    // Unconditional: the box is hidden in the markup now, so a path that
    // leaves the gallery unclaimed is the only thing that can show it —
    // including the in-card case, where it is already visible and this is a
    // no-op.
    var fallBack = function () {
      if (self.previewEl && !self.galleryTargets) self.previewEl.removeAttribute('hidden');
    };
    if (!this.takeOverGallery || !this.views || !this.views.length) { fallBack(); return; }
    var media = this.visibleMedia();
    if (!media.length) { fallBack(); return; }
    var thumbs = Array.prototype.slice.call(document.querySelectorAll(this.gallerySelectors.thumb));
    var byMediaId = {};
    thumbs.forEach(function (t) { var id = t.getAttribute('data-media-id'); if (id && !byMediaId[id]) byMediaId[id] = t; });
    var targets = [];
    for (var i = 0; i < this.views.length && i < media.length; i++) {
      var m = media[i];
      var imgs = Array.prototype.slice.call(m.querySelectorAll('img'));
      var id = m.getAttribute('data-media-id');
      var thumb = id && byMediaId[id] ? byMediaId[id] : (thumbs[i] || null);
      if (thumb) imgs = imgs.concat(Array.prototype.slice.call(thumb.querySelectorAll('img')));
      if (!imgs.length) continue;
      // The frame keeps the original photo's aspect (the theme sized its box
      // from it), so the preview is drawn at that shape with the mug photo
      // fitted inside.
      var main = imgs[0];
      var fw = Number(main.getAttribute('width')) || main.naturalWidth || 0;
      var fh = Number(main.getAttribute('height')) || main.naturalHeight || 0;
      targets.push({ view: i, imgs: imgs, canvas: document.createElement('canvas'), url: null, aspect: (fw > 0 && fh > 0) ? fh / fw : null });
    }
    if (!targets.length) { fallBack(); return; }
    this.galleryTargets = targets;
    this.galleryShown = media;
    this.watchGallery();
    if (this.previewEl) this.previewEl.setAttribute('hidden', '');
    // A loader put on an image the preview does not take after all goes now.
    if (this.loaders) {
      var self2 = this;
      this.loaders.filter(function (l) { return !targets.some(function (t) { return t.view === l.view; }); })
        .forEach(function (l) { self2.hideGenerating(l.view); });
    }
  };

  // THE PHOTOS SHOWING NOW, WHENEVER THE THEME CHANGES THEM (Philip, 23 Oct
  // 2026: the gallery "sometimes staying on the opening colour's hidden photos
  // after a colour change"). Warehouse hides the other colours' photos in an
  // animation frame (fastdom), and a tab in the background runs none. The
  // colour changed while the tab was out of sight, so the redrawn block started
  // on its timers and took the photos still showing: the opening colour's. When
  // the tab came back the theme hid those, and the shopper's mug went on being
  // drawn into photos nobody could see, under the new colour's stock photos
  // ("Name"). The one follow-up after variant:changed (220 ms) had long gone.
  // So the photos are watched: when the theme changes which of them show, the
  // preview moves to the ones showing now. Watching the photos themselves, not
  // their box — Warehouse's carousel moves them into a new box on each colour.
  function sameElements(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }
  Personaliser.prototype.watchGallery = function () {
    if (!this.takeOverGallery || typeof MutationObserver === 'undefined') return;
    var self = this;
    if (!this.galleryWatch) {
      var timer = null;
      this.galleryWatch = new MutationObserver(function () {
        // A block the theme has drawn again since: its successor watches now.
        if (!self.root.isConnected) { self.galleryWatch.disconnect(); return; }
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (!self.root.isConnected || !self.galleryTargets) return;
          if (!sameElements(self.visibleMedia(), self.galleryShown)) self.reclaimGallery();
        }, 40);
      });
    }
    var watch = this.galleryWatch;
    Array.prototype.forEach.call(document.querySelectorAll(this.gallerySelectors.media), function (el) {
      watch.observe(el, { attributes: true, attributeFilter: ['class', 'hidden'] });
    });
  };

  // A LOADER ON EACH IMAGE BEING MADE, instead of words on the photo — Philip,
  // 29 Sep 2026: "the side images and main image should have nice loaders so
  // that when they are generating or loading it shows this instead of text".
  // The gallery images the preview is about to take over — the main one and
  // each thumbnail — get a veil with a shimmer (the main one a ring as well),
  // and each goes as its own frame lands (paintGallery). The wait is said to
  // screen readers only. It fades in after a beat (the CSS), so a change that
  // is quick never flashes it.
  function addLoader(el, kind) {
    if (!el) return null;
    for (var i = 0; i < el.children.length; i++) {
      if (el.children[i].classList && el.children[i].classList.contains('towsons-personaliser__loader')) return el.children[i];
    }
    var l = document.createElement('div');
    l.className = 'towsons-personaliser__loader is-' + kind;
    l.setAttribute('aria-hidden', 'true');
    if (kind === 'main') l.innerHTML = '<span class="towsons-personaliser__loader-ring"></span>';
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
    el.appendChild(l);
    return l;
  }
  function dropLoader(l) {
    if (!l || !l.parentNode) return;
    l.classList.add('is-leaving');
    setTimeout(function () { if (l.parentNode) l.parentNode.removeChild(l); }, 300);
  }

  // The first n gallery images with their thumbnails — paired as claimGallery
  // pairs them: by media id, else by position.
  Personaliser.prototype.galleryPairs = function (n) {
    var media = this.visibleMedia();
    var thumbs = Array.prototype.slice.call(document.querySelectorAll(this.gallerySelectors.thumb));
    var byMediaId = {};
    thumbs.forEach(function (t) { var id = t.getAttribute('data-media-id'); if (id && !byMediaId[id]) byMediaId[id] = t; });
    var out = [];
    for (var i = 0; i < n && i < media.length; i++) {
      var id = media[i].getAttribute('data-media-id');
      out.push({ view: i, media: media[i], thumb: id && byMediaId[id] ? byMediaId[id] : (thumbs[i] || null) });
    }
    return out;
  };

  // Said, for screen readers — nothing on the photo says it.
  Personaliser.prototype.say = function (msg) {
    if (!this.srStatus && this.root) {
      this.srStatus = document.createElement('span');
      this.srStatus.className = 'towsons-personaliser__sr';
      this.srStatus.setAttribute('role', 'status');
      this.srStatus.setAttribute('aria-live', 'polite');
      this.root.appendChild(this.srStatus);
    }
    if (this.srStatus) this.srStatus.textContent = msg || '';
  };

  Personaliser.prototype.showGenerating = function () {
    if (this.loaders || !this.takeOverGallery) return;
    // As many images as the preview will take: its views for this colour
    // (known before the renderer is), or what it has already claimed.
    var n = this.galleryTargets ? this.galleryTargets.length
      : (viewsFor(this.cfg || {}, blankFor(this.cfg, this.variantId)).length || 1);
    var pairs = this.galleryPairs(n);
    if (!pairs.length) return;
    this.loaders = pairs.map(function (p) {
      return { view: p.view, els: [addLoader(p.media, p.view === 0 ? 'main' : 'media'), addLoader(p.thumb, 'thumb')].filter(Boolean) };
    });
    this.say(this.root.getAttribute('data-generating-label') || 'Generating your preview…');
  };
  // One image's loader (its frame has landed), or all of them.
  Personaliser.prototype.hideGenerating = function (view) {
    if (!this.loaders) return;
    var keep = [];
    this.loaders.forEach(function (l) {
      if (view == null || l.view === view) l.els.forEach(dropLoader); else keep.push(l);
    });
    this.loaders = keep.length ? keep : null;
    if (!this.loaders) this.say('');
  };

  // One composite per view at the photo's own resolution (capped), handed to
  // every <img> that shows that view as a blob URL; srcset/sizes are dropped
  // so the browser cannot swap the photo back in.
  Personaliser.prototype.paintGallery = function () {
    var self = this;
    var treat = this.treatAtRest();
    this.galleryTargets.forEach(function (t) {
      var view = self.views[t.view];
      var natW = view.img.naturalWidth || view.img.width;
      self.renderer.compositeInto(t.canvas, view, Math.min(natW, 1200), 1, t.aspect, treat);
      t.canvas.toBlob(function (blob) {
        if (!blob) return;
        var url = URL.createObjectURL(blob);
        t.imgs.forEach(function (img) {
          img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.removeAttribute('data-srcset');
          ['data-zoom', 'data-src', 'data-image', 'data-large', 'data-original'].forEach(function (a) { if (img.hasAttribute(a)) img.setAttribute(a, url); });
          img.src = url;
        });
        if (t.url) URL.revokeObjectURL(t.url);
        t.url = url;
        self.hideGenerating(t.view);
        if (t.view === 0 && self.treatLayerDone) {
          var first = t.imgs[0];
          (first && first.decode ? first.decode().catch(function () {}) : Promise.resolve()).then(function () { self.dropTreatLayer(); });
        }
      }, 'image/jpeg', 0.9);
    });
  };

  Personaliser.prototype.buildViewButtons = function () {
    var self = this;
    if (!this.viewsEl) return;
    this.viewsEl.innerHTML = '';
    if (this.galleryTargets || !this.views || this.views.length < 2) return;
    this.views.forEach(function (v, idx) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'towsons-personaliser__view';
      b.textContent = v.meta.name || ('View ' + (idx + 1));
      b.setAttribute('aria-pressed', idx === self.viewIndex ? 'true' : 'false');
      b.addEventListener('click', function () {
        self.viewIndex = idx;
        Array.prototype.forEach.call(self.viewsEl.children, function (c, i) { c.setAttribute('aria-pressed', i === idx ? 'true' : 'false'); });
        self.composite();
      });
      self.viewsEl.appendChild(b);
    });
  };

  Personaliser.prototype.render = function () {
    if (!this.ready || !this.live) return;
    var self = this;
    if (this.pending) return;
    this.pending = true;
    requestAnimationFrame(function () {
      self.pending = false;
      try { self.renderer.apply(self.artworkValues(), self.fillValues()); }
      catch (e) { if (window.console && console.warn) console.warn('[towsons-personaliser] layout failed:', e && e.message ? e.message : e); }
      self.composite();
    });
  };

  Personaliser.prototype.composite = function () {
    if (this.galleryTargets) { this.paintGallery(); return; }
    var cv = this.canvas;
    var cssW = Math.max(200, Math.min(600, cv.parentElement ? cv.parentElement.clientWidth - 16 : 600));
    this.renderer.compositeInto(cv, this.views[this.viewIndex], cssW, Math.min(window.devicePixelRatio || 1, 2), null, this.treat);
  };

  // The treats dropdown sits with the options, outside this block, and the
  // theme redraws it — found fresh each time rather than kept.
  Personaliser.prototype.treatsRoot = function () {
    var form = this.form;
    var all = Array.prototype.slice.call(document.querySelectorAll('[data-towsons-treats]:not([data-line-mode])'));
    return all.filter(function (r) { return form && form.contains(r); })[0] || all[0] || null;
  };
  Personaliser.prototype.treatChoice = function () { return treatChoiceIn(this.treatsRoot()); };
  // The treat at rest — half out — which is what every still picture shows.
  Personaliser.prototype.treatAtRest = function () { return this.treat && this.treat.img ? { img: this.treat.img, rise: 1 } : null; };
  Personaliser.prototype.treatName = function () { return this.treat ? this.treat.name : ''; };
  Personaliser.prototype.onTreatChange = function () {
    var choice = this.treatChoice();
    if (choice) this.goLive();
    this.setTreat(choice, true);
  };
  // A new choice: load its picture, then either pop it up or show it at rest.
  // A choice made while an earlier one is still loading wins.
  Personaliser.prototype.setTreat = function (choice, animate) {
    var self = this;
    var seq = ++this.treatSeq;
    if (!choice) {
      if (this.treat) { this.treat = null; this.endTreatAnimation(); }
      return Promise.resolve();
    }
    if (this.treat && this.treat.url === choice.url && this.treat.name === choice.name) return Promise.resolve();
    return loadTreat(choice.url).then(function (img) {
      if (seq !== self.treatSeq) return;
      if (!img) { self.treat = null; self.endTreatAnimation(); return; }
      self.treat = { name: choice.name, url: choice.url, img: img, rise: animate ? 0 : 1 };
      if (animate) self.animateTreat(); else self.render();
    });
  };
  // NOTHING MAY BE LEFT MID-POP.
  //
  // Animation frames stop in a tab nobody is looking at, so a pop started as
  // the shopper switched away would freeze with the treat half-risen, the
  // moving layer still over the gallery and "Generating your preview…" still
  // up. A hidden page skips straight to rest, and a pop that has not finished
  // in twice its own length is finished for it.
  Personaliser.prototype.animateTreat = function () {
    var self = this, t0 = null, DUR = 520, seq = this.treatSeq;
    if (document.hidden) { this.treat.rise = 1; this.endTreatAnimation(); return; }
    var settle = setTimeout(function () {
      if (seq === self.treatSeq && self.treat && self.treat.rise < 1) { self.treat.rise = 1; self.endTreatAnimation(); }
    }, DUR * 2);
    var step = function (ts) {
      if (seq !== self.treatSeq || !self.treat) { clearTimeout(settle); self.endTreatAnimation(); return; }
      if (t0 === null) t0 = ts;
      var t = Math.min(1, (ts - t0) / DUR);
      self.treat.rise = easeOutBack(t);
      self.drawTreatFrame();
      if (t < 1) requestAnimationFrame(step);
      else { clearTimeout(settle); self.treat.rise = 1; self.endTreatAnimation(); }
    };
    requestAnimationFrame(step);
  };
  // A frame of the pop. The gallery is painted through blob URLs, one per
  // view — far too slow for sixty frames — so while the treat is moving it is
  // drawn on a canvas laid over the first gallery image, and the gallery
  // itself is painted once, at rest, when the pop has finished.
  Personaliser.prototype.drawTreatFrame = function () {
    if (!this.renderer || !this.renderer.ready) return;
    if (!this.galleryTargets) { this.composite(); return; }
    var t = this.galleryTargets[0], img = t && t.imgs[0];
    if (!img || !img.parentElement) return;
    var layer = this.treatLayer;
    if (!layer) {
      layer = this.treatLayer = document.createElement('canvas');
      layer.className = 'towsons-personaliser__treat-layer';
      var host = img.parentElement;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(layer);
    }
    var r = img.getBoundingClientRect(), hr = layer.parentElement.getBoundingClientRect();
    layer.style.left = Math.round(r.left - hr.left) + 'px';
    layer.style.top = Math.round(r.top - hr.top) + 'px';
    this.renderer.compositeInto(layer, this.views[t.view], Math.max(1, Math.round(r.width)), Math.min(window.devicePixelRatio || 1, 2), t.aspect, this.treat);
  };
  // The pop is over: paint the gallery at rest, and take the moving layer
  // away only once that paint is on screen, so nothing flashes in between.
  Personaliser.prototype.endTreatAnimation = function () {
    this.treatLayerDone = !!this.treatLayer;
    this.render();
    if (this.treatLayer && !this.galleryTargets) this.dropTreatLayer();
  };
  Personaliser.prototype.dropTreatLayer = function () {
    if (this.treatLayer && this.treatLayer.parentNode) this.treatLayer.parentNode.removeChild(this.treatLayer);
    this.treatLayer = null; this.treatLayerDone = false;
  };

  // ─────────────────────────────────────────────────────────────────────
  // Basket lines. The wiring drops a marker next to each personalised line's
  // image: data-config (the product's metafield), data-values (the carried
  // {t,c,v}). The image is swapped for the preview: from the session cache
  // written at add-to-basket when there is one, else rendered here.
  // ─────────────────────────────────────────────────────────────────────
  function paintLine(marker) {
    var cfg = parseJsonAttr(marker, 'data-config');
    var carried = parseJsonAttr(marker, 'data-values');
    if (!cfg || !cfg.bundle_url) return;
    var values = (carried && carried.v) || {};
    var img = null;
    for (var n = marker.parentElement; n && n !== document.body && !img; n = n.parentElement) img = n.querySelector('img');
    if (!img) return;
    var setSrc = function (url) {
      img.removeAttribute('srcset'); img.removeAttribute('sizes');
      img.__towsonsSrc = url;
      img.src = url;
    };
    // Painted already — unless the theme has since put its own picture back
    // on the element, in which case ours goes back on top.
    if (marker.__towsonsPainted) {
      if (img.__towsonsSrc && img.getAttribute('src') !== img.__towsonsSrc) setSrc(img.__towsonsSrc);
      return;
    }
    marker.__towsonsPainted = true;
    var view = Number(marker.getAttribute('data-view')) || 0;
    var variantId = marker.getAttribute('data-variant-id');
    var blank = blankFor(cfg, variantId);
    var scripts = { fabric: marker.getAttribute('data-fabric-src'), tcd: marker.getAttribute('data-tcd-src') };
    var fw = Number(img.getAttribute('width')) || img.naturalWidth || 0;
    var fh = Number(img.getAttribute('height')) || img.naturalHeight || 0;
    var aspect = (fw > 0 && fh > 0) ? fh / fw : null;
    // Drawn under the same rules as the product page and the modal (a prefix
    // the artwork carries, say), so the three never disagree about the mug.
    var by = slotsByN(cfg), drawn = {};
    Object.keys(values).forEach(function (k) { drawn[k] = by[k] ? artworkValue(values[k], by[k]) : values[k]; });
    var persisted = marker.getAttribute('data-preview-url');
    var treatName = marker.getAttribute('data-treat') || '', treatUrl = marker.getAttribute('data-treat-image') || '';
    var withTreat = function (img2) { return img2 ? { img: img2, rise: 1 } : null; };
    // A picture drawn small (the "More colours" mugs) asks for a smaller frame
    // and smaller mug photos; a basket line draws at 360 from the full photos.
    var frameW = Number(marker.getAttribute('data-frame-width')) || 360;
    var small = { photoWidth: Number(marker.getAttribute('data-photo-width')) || 0, firstViewOnly: view === 0 };
    var cached = cacheGet(previewCacheKey(cfg.template_id, cfg.change_id, values, view, blank, treatName, frameW));
    var shown = false;
    if (cached) { setSrc(cached); shown = true; }
    else if (Renderer.has(cfg, variantId)) {
      // The renderer that drew the product page is still in memory: the line
      // is drawn from it now, rather than left showing the stock photo while
      // the stored copy comes down.
      shown = true;
      Promise.all([Renderer.get(cfg, scripts, variantId), loadTreat(treatUrl)]).then(function (r) {
        if (persisted && img.__towsonsSrc === persisted) return;
        setSrc(r[0].frame(drawn, view, frameW, aspect, withTreat(r[1])).toDataURL('image/jpeg', 0.85));
      }).catch(function () { if (persisted) setSrc(persisted); });
    }
    if (persisted) {
      if (!shown) { setSrc(persisted); return; }
      // Swap to the stored copy only once it is actually in hand.
      var pre = new Image();
      pre.onload = function () { setSrc(persisted); };
      pre.src = persisted;
      return;
    }
    if (shown) return;
    Promise.all([Renderer.get(cfg, scripts, variantId, small), loadTreat(treatUrl)]).then(function (r) {
      var renderer = r[0];
      var cv = renderer.frame(drawn, view, frameW, aspect, withTreat(r[1]));
      var url = cv.toDataURL('image/jpeg', 0.85);
      if (renderer.fontsOk) {
        cacheSet(previewCacheKey(cfg.template_id, cfg.change_id, values, view, blank, treatName, frameW), url);
        // A picture its owner keeps between visits (the "More colours" mugs) is said to be
        // drawn right: towsons-more-colours.js keeps it.
        if (marker.hasAttribute('data-keep')) {
          try { marker.dispatchEvent(new CustomEvent('towsons:painted', { bubbles: true, detail: { url: url } })); } catch (e) { /* not kept */ }
        }
      }
      setSrc(url);
    }).catch(function (e) {
      marker.__towsonsPainted = false;
      if (window.console && console.warn) console.warn('[towsons-personaliser] basket preview unavailable:', e && e.message ? e.message : e);
    });
  }

  function paintLines() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-towsons-line]'), paintLine);
    Array.prototype.forEach.call(document.querySelectorAll('[data-towsons-line-edit]'), function (el) {
      if (!el.__towsonsLineEditor) el.__towsonsLineEditor = new LineEditor(el);
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Editing in the basket. "Edit Personalisation" opens the fields under the
  // line; the line's image follows the typing; Save writes the properties to
  // that line (cart/change.js keeps quantity and the other properties) and
  // asks the theme to redraw the basket. Without JS the control is a link to
  // the product page in edit mode.
  // ─────────────────────────────────────────────────────────────────────
  // Liquid serialises line_item.properties as [[name, value], …]; older
  // themes may hand over an object. Either way: a plain map.
  // The chosen treat in a treats dropdown: its name and the picture that
  // rises out of the mug (the cutout, else the plain picture). "No thanks"
  // is no treat.
  function treatChoiceIn(root) {
    if (!root) return null;
    var input = root.querySelector('[data-treats-value]');
    var name = input ? input.value : '';
    if (!name) return null;
    var row = null;
    Array.prototype.forEach.call(root.querySelectorAll('[role="option"]'), function (o) { if (!row && o.getAttribute('data-value') === name) row = o; });
    if (!row || row.getAttribute('data-none') === 'true') return null;
    var url = row.getAttribute('data-cutout') || row.getAttribute('data-image') || '';
    return url ? { name: name, url: url } : null;
  }

  function normaliseProps(p) {
    var out = {};
    if (Array.isArray(p)) { p.forEach(function (pair) { if (pair && pair.length >= 2 && pair[0] != null) out[String(pair[0])] = pair[1] == null ? '' : String(pair[1]); }); }
    else if (p && typeof p === 'object') { Object.keys(p).forEach(function (k) { out[k] = p[k] == null ? '' : String(p[k]); }); }
    // A purely numeric key is never a real property — it is the residue of a
    // pair array once treated as an object; leave it behind.
    Object.keys(out).forEach(function (k) { if (/^\d+$/.test(k)) delete out[k]; });
    return out;
  }

  // THE PAGE UNDER THE MODAL STAYS WHERE IT IS.
  //
  // On a phone the keyboard shrinks the visible area, and Safari scrolls
  // whatever it can to bring the field into view — the page behind the
  // dialog included, and a dialog sized to the layout viewport is left half
  // under the keyboard. The body is pinned while a modal is open, and the
  // dialog follows the visual viewport so its bottom edge is the keyboard's
  // top edge.
  var PAGE_LOCK = { count: 0, y: 0, was: null };
  function lockPage() {
    if (PAGE_LOCK.count++ > 0) return;
    var b = document.body;
    PAGE_LOCK.y = window.scrollY || window.pageYOffset || 0;
    PAGE_LOCK.was = { position: b.style.position, top: b.style.top, left: b.style.left, right: b.style.right, width: b.style.width, overflow: b.style.overflow };
    b.style.position = 'fixed'; b.style.top = (-PAGE_LOCK.y) + 'px'; b.style.left = '0'; b.style.right = '0'; b.style.width = '100%'; b.style.overflow = 'hidden';
    document.documentElement.classList.add('towsons-modal-open');
  }
  function unlockPage() {
    if (PAGE_LOCK.count === 0) return;
    if (--PAGE_LOCK.count > 0) return;
    var b = document.body, w = PAGE_LOCK.was || {};
    b.style.position = w.position || ''; b.style.top = w.top || ''; b.style.left = w.left || ''; b.style.right = w.right || ''; b.style.width = w.width || ''; b.style.overflow = w.overflow || '';
    document.documentElement.classList.remove('towsons-modal-open');
    try { window.scrollTo({ top: PAGE_LOCK.y, left: 0, behavior: 'instant' }); } catch (e) { window.scrollTo(0, PAGE_LOCK.y); }
  }

  function LineEditor(el) {
    this.el = el;
    this.cfg = parseJsonAttr(el, 'data-config') || {};
    this.carried = parseJsonAttr(el, 'data-values') || {};
    this.properties = normaliseProps(parseJsonAttr(el, 'data-properties'));
    this.key = el.getAttribute('data-key');
    this.quantity = Number(el.getAttribute('data-quantity')) || 1;
    this.scripts = { fabric: el.getAttribute('data-fabric-src'), tcd: el.getAttribute('data-tcd-src') };
    this.openEl = el.querySelector('[data-line-edit-open]');
    this.dialog = el.querySelector('[data-line-edit-form]');
    this.panel = el.querySelector('[data-modal-panel]');
    this.inputs = Array.prototype.slice.call(el.querySelectorAll('[data-line-slot]'));
    bindFieldRules(this.inputs.filter(function (i) { return i.tagName === 'INPUT'; }), this.cfg, 'data-line-slot');
    this.variantSel = el.querySelector('[data-line-variant]');
    this.doneBtn = el.querySelector('[data-line-edit-done]');
    this.closeBtn = el.querySelector('[data-line-edit-close]');
    this.statusEl = el.querySelector('[data-line-edit-status]');
    this.treatHidden = el.querySelector('[data-line-treat]');
    this.treatsRoot = el.querySelector('[data-towsons-treats]');
    this.treat = null;
    this.treatSeq = 0;
    this.previewEl = el.querySelector('[data-modal-preview]');
    this.generatingEl = el.querySelector('[data-modal-generating]');
    this.frames = null;
    this.img = null;
    this.line = null;
    // The line's picture, never one of the editor's own (its style list has thumbnails, and on Horizon the
    // editor sits in the details cell, beside the picture's).
    for (var n = el.parentElement; n && n !== document.body && !this.img; n = n.parentElement) {
      this.img = Array.prototype.filter.call(n.querySelectorAll('img'), function (i) { return !el.contains(i); })[0] || null;
      if (this.img) this.line = n;
    }
    this.lastSaved = this.snapshot();
    this.dirtySinceRefresh = false;
    this.saving = null;
    this.isOpen = false;
    var self = this;
    if (this.openEl) this.openEl.addEventListener('click', function (e) { e.preventDefault(); self.open(); });
    if (this.doneBtn) this.doneBtn.addEventListener('click', function () { self.close(); });
    if (this.closeBtn) this.closeBtn.addEventListener('click', function () { self.close(); });
    if (this.dialog) {
      // A click on the backdrop (the dialog itself, outside the panel) closes.
      this.dialog.addEventListener('click', function (e) { if (e.target === self.dialog) self.close(); });
      this.dialog.addEventListener('cancel', function (e) { e.preventDefault(); self.close(); });
    }
    var preview = debounce(function () { self.preview(); }, 60);
    var autosave = debounce(function () { self.save(); }, 900);
    this.inputs.forEach(function (i) {
      i.addEventListener('input', function () { preview(); autosave(); });
      i.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); self.close(); } });
    });
    if (this.treatHidden) this.treatHidden.addEventListener('change', function () { self.setTreat(treatChoiceIn(self.treatsRoot), true).then(function () { self.save(); }); });
    if (this.variantSel) this.variantSel.addEventListener('change', function () { self.useVariant(self.variantSel.value).then(function () { self.save(); }); });
  }
  LineEditor.prototype.values = function () {
    var v = {};
    this.inputs.forEach(function (i) { v[i.getAttribute('data-line-slot')] = i.value; });
    return v;
  };

  // The basket editor draws the same artwork as the product page, so it goes
  // through the same rules. Its inputs carry data-line-slot rather than
  // data-slot, which is the only difference.
  LineEditor.prototype.artworkValues = function () {
    var by = slotsByN(this.cfg), v = this.values(), out = {};
    Object.keys(v).forEach(function (k) { out[k] = artworkValue(v[k], by[k]); });
    return out;
  };
  // What a save would write: the field values, the treat and the variant.
  LineEditor.prototype.snapshot = function () {
    return JSON.stringify({ v: this.values(), treat: this.treatHidden ? this.treatHidden.value : null, variant: this.variantSel ? this.variantSel.value : null });
  };
  LineEditor.prototype.setError = function (msg) {
    if (this.statusEl) this.statusEl.textContent = msg || '';
  };
  LineEditor.prototype.open = function () {
    if (!this.dialog || this.isOpen) return;
    this.isOpen = true;
    this.setError('');
    // The page is pinned before anything can scroll it (a focus can).
    lockPage();
    // Native <dialog> sits in the top layer, above the drawer and untouched
    // by any transformed ancestor; older browsers get the fixed fallback.
    if (typeof this.dialog.showModal === 'function') { try { this.dialog.showModal(); } catch (e) { this.dialog.setAttribute('open', ''); } }
    else this.dialog.setAttribute('open', '');
    this.followViewport(true);
    // THE KEYBOARD DOES NOT COME UP UNINVITED. Focusing the name field on
    // open threw the phone's keyboard over half the modal before the shopper
    // had even seen their mug. The PANEL takes focus instead, so the dialog
    // still holds the keyboard's tab order and Escape, and the field is there
    // when they want it.
    if (this.panel && this.panel.focus) { try { this.panel.focus({ preventScroll: true }); } catch (e) { this.panel.focus(); } }
    this.setTreat(treatChoiceIn(this.treatsRoot), false);
    this.useVariant(this.variantSel ? this.variantSel.value : null);
  };
  // The treat in the modal: the same load-then-pop as the product page, the
  // frames redrawn directly (two small canvases are cheap to redraw).
  LineEditor.prototype.treatAtRest = function () { return this.treat && this.treat.img ? { img: this.treat.img, rise: 1 } : null; };
  LineEditor.prototype.treatName = function () { return this.treat ? this.treat.name : ''; };
  LineEditor.prototype.setTreat = function (choice, animate) {
    var self = this;
    var seq = ++this.treatSeq;
    if (!choice) { if (this.treat) { this.treat = null; this.preview(); } return Promise.resolve(); }
    if (this.treat && this.treat.url === choice.url && this.treat.name === choice.name) return Promise.resolve();
    return loadTreat(choice.url).then(function (img) {
      if (seq !== self.treatSeq) return;
      if (!img) { self.treat = null; self.preview(); return; }
      self.treat = { name: choice.name, url: choice.url, img: img, rise: animate ? 0 : 1 };
      if (!animate || document.hidden) { self.treat.rise = 1; self.preview(); return; }
      var t0 = null, DUR = 520;
      var settle = setTimeout(function () {
        if (seq === self.treatSeq && self.treat && self.treat.rise < 1) { self.treat.rise = 1; self.preview(); }
      }, DUR * 2);
      var step = function (ts) {
        if (seq !== self.treatSeq || !self.treat) { clearTimeout(settle); return; }
        if (t0 === null) t0 = ts;
        var t = Math.min(1, (ts - t0) / DUR);
        self.treat.rise = t < 1 ? easeOutBack(t) : 1;
        self.preview();
        if (t < 1) requestAnimationFrame(step); else clearTimeout(settle);
      };
      requestAnimationFrame(step);
    });
  };
  // THE STYLE PICKER CHANGES THE MUG, SO IT CHANGES THE PICTURE.
  //
  // A new colour is a new set of mug photos: the renderer for that blank is
  // fetched (or found already loaded), the frames are rebuilt on it, and only
  // then is the line saved — the preview file that goes on the line has to be
  // the mug they will get. A second pick while the first is still loading
  // wins; the earlier one is dropped when it arrives.
  LineEditor.prototype.useVariant = function (variantId) {
    var self = this;
    var req = this._variantReq = (this._variantReq || 0) + 1;
    if (this.generatingEl && !Renderer.has(this.cfg, variantId)) this.generatingEl.removeAttribute('hidden');
    var p = Renderer.get(this.cfg, this.scripts, variantId).then(function (r) {
      if (req !== self._variantReq) return;
      self.renderer = r;
      self.resetFrames();
      self.buildFrames();
      self.preview();
    }).catch(function () {
      if (self.generatingEl) self.generatingEl.setAttribute('hidden', '');
    }).then(function () { if (self._swapping === p) self._swapping = null; });
    this._swapping = p;
    return p;
  };
  LineEditor.prototype.resetFrames = function () {
    var self = this;
    (this.frames || []).forEach(function (f) { if (f.wrap.parentNode === self.previewEl) self.previewEl.removeChild(f.wrap); });
    this.frames = null;
  };
  // The dialog is sized to what the shopper can actually see: with the
  // keyboard up that is the visual viewport, not the page.
  LineEditor.prototype.followViewport = function (on) {
    var vv = window.visualViewport, self = this;
    if (!vv || !this.dialog) return;
    if (on) {
      if (!this._vvHandler) {
        this._vvHandler = function () {
          var d = self.dialog;
          d.style.top = Math.round(vv.offsetTop) + 'px';
          d.style.left = Math.round(vv.offsetLeft) + 'px';
          d.style.width = Math.round(vv.width) + 'px';
          d.style.height = Math.round(vv.height) + 'px';
        };
      }
      vv.addEventListener('resize', this._vvHandler);
      vv.addEventListener('scroll', this._vvHandler);
      this._vvHandler();
    } else if (this._vvHandler) {
      vv.removeEventListener('resize', this._vvHandler);
      vv.removeEventListener('scroll', this._vvHandler);
      ['top', 'left', 'width', 'height'].forEach(function (p) { self.dialog.style[p] = ''; });
    }
  };
  // Finishing waits for any save still in flight, then closes and lets the
  // theme redraw the basket (its controls are bound to the line key, which a
  // save changes).
  LineEditor.prototype.close = function () {
    var self = this;
    if (!this.isOpen) return;
    var finish = function () {
      self.isOpen = false;
      if (self.doneBtn) { self.doneBtn.removeAttribute('disabled'); self.doneBtn.classList.remove('is-busy'); }
      if (typeof self.dialog.close === 'function' && self.dialog.open) { try { self.dialog.close(); } catch (e) { self.dialog.removeAttribute('open'); } }
      else self.dialog.removeAttribute('open');
      self.followViewport(false);
      unlockPage();
      if (self.dirtySinceRefresh) self.refreshBasket();
    };
    if (this.doneBtn) { this.doneBtn.setAttribute('disabled', ''); this.doneBtn.classList.add('is-busy'); }
    var pending = (this.snapshot() !== this.lastSaved) ? this.save() : (this.saving || Promise.resolve());
    pending.then(finish, finish);
  };
  // One frame per mug view, side by side, so the shopper sees the whole mug.
  LineEditor.prototype.buildFrames = function () {
    if (!this.previewEl || !this.renderer || this.frames) return;
    var views = this.renderer.views.length ? this.renderer.views : [null];
    this.frames = views.map(function (v) {
      var wrap = document.createElement('div');
      wrap.className = 'towsons-personaliser__frame';
      var cv = document.createElement('canvas');
      cv.setAttribute('aria-label', 'Preview of your personalised ' + (((window.Towsons || {}).words || {}).item || 'mug'));
      wrap.appendChild(cv);
      if (v && v.meta && v.meta.name && views.length > 1) {
        var cap = document.createElement('span');
        cap.className = 'towsons-personaliser__frame-label';
        cap.textContent = v.meta.name;
        wrap.appendChild(cap);
      }
      return { view: v, canvas: cv, wrap: wrap };
    });
    var self = this;
    this.frames.forEach(function (f) { self.previewEl.appendChild(f.wrap); });
    this.previewEl.classList.toggle('has-two', this.frames.length > 1);
  };
  // The modal's own preview; the basket line's image follows once saved.
  LineEditor.prototype.preview = function () {
    if (!this.renderer || !this.renderer.ready || !this.frames) return;
    var self = this;
    try {
      this.renderer.apply(this.artworkValues());
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      // Sized from the box the frames share, split between them. The canvas
      // is given its width AND its height, so nothing downstream may stretch
      // it — a stylesheet cap once narrowed a square frame into a 160×168
      // box and every mug in the modal came out pinched.
      var n = this.frames.length, box = this.previewEl;
      var gap = parseFloat(window.getComputedStyle(box).columnGap) || 0;
      var w = Math.max(120, Math.min(260, Math.floor(((box.clientWidth || 300) - gap * (n - 1)) / n)));
      var treat = this.treat;
      this.frames.forEach(function (f) { self.renderer.compositeInto(f.canvas, f.view, w, dpr, null, treat); });
      if (this.generatingEl) this.generatingEl.setAttribute('hidden', '');
    } catch (e) { /* keep the last frame */ }
  };
  // The visible "Name: …" / "Treat: …" lines follow the save without a redraw.
  LineEditor.prototype.reflectProperties = function (props) {
    if (!this.line) return;
    var items = this.line.querySelectorAll('li');
    Object.keys(props).forEach(function (k) {
      if (k.charAt(0) === '_') return;
      Array.prototype.forEach.call(items, function (li) {
        var t = li.textContent || '';
        if (t.indexOf(k + ':') === 0) li.textContent = k + ': ' + props[k];
      });
    });
  };
  LineEditor.prototype.save = function () {
    var self = this;
    if (this.saving) return this.saving.then(function () { return self.snapshot() !== self.lastSaved ? self.save() : null; });
    var missing = this.inputs.filter(function (i) { return i.required && !i.value.trim(); });
    if (missing.length) {
      var lab = missing[0].closest('label') && missing[0].closest('label').querySelector('.towsons-personaliser__line-label');
      this.setError('Please fill in ' + (lab ? lab.textContent : 'the text') + '.');
      return Promise.resolve();
    }
    this.setError('');
    var snap = this.snapshot();
    if (snap === this.lastSaved) return Promise.resolve();
    // `values` is what they typed — the cache key, the properties and the
    // payload all record that. `drawn` is what the canvas paints.
    var values = this.values(), drawn = this.artworkValues(), compact = {};
    Object.keys(values).forEach(function (k) { if (values[k] !== '') compact[k] = values[k]; });
    var props = {};
    Object.keys(this.properties).forEach(function (k) { props[k] = self.properties[k]; });
    this.inputs.forEach(function (i) { props[i.getAttribute('data-property')] = i.value; });
    if (this.treatHidden && this.treatHidden.getAttribute('data-line-treat')) props[this.treatHidden.getAttribute('data-line-treat')] = this.treatHidden.value;
    props._towsons_personaliser = JSON.stringify({ t: this.cfg.template_id, c: this.cfg.change_id, v: compact });
    var variantId = this.variantSel ? this.variantSel.value : String(this.key || '').split(':')[0];
    var blank = blankFor(this.cfg, variantId), treatName = this.treatName();
    var oldKey = this.key;
    // A line's properties cannot take a new file through cart/change, so the
    // edit becomes a new line carrying a fresh preview file (and the chosen
    // variant), and the old line goes once the new one is in. If the style
    // picker has a new colour's photos still on their way, the file waits for
    // them: what goes on the line is the mug they will get.
    var blobP = (this._swapping || Promise.resolve()).then(function () {
      var r = self.renderer, treat = self.treatAtRest();
      treatName = self.treatName();   // the swap may have finished since
      if (!r || !r.ready) return null;
      // The basket will show this frame straight away after the redraw.
      if (r.fontsOk) {
        for (var v = 0; v < Math.max(1, r.views.length); v++) {
          try { cacheSet(previewCacheKey(self.cfg.template_id, self.cfg.change_id, values, v, blank, treatName), r.frame(drawn, v, 360, null, treat).toDataURL('image/jpeg', 0.85)); } catch (e) {}
        }
        self.preview();
      }
      return canvasToBlob(r.frame(drawn, 0, 600, null, treat));
    });
    this.saving = blobP.then(function (blob) {
      var fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', String(self.quantity));
      Object.keys(props).forEach(function (k) { if (k !== '_preview') fd.append('properties[' + k + ']', props[k]); });
      if (blob) fd.append('properties[_preview]', blob, 'preview.jpg');
      return fetch(cartUrl('add'), { method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
    }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (added) {
        return fetch(cartUrl('change'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: oldKey, quantity: 0 }),
        }).then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); }).then(function (cart) { cart.__added = added; return cart; });
      })
      .then(function (cart) {
        var added = cart.__added || {};
        var mine = (cart.items || []).filter(function (it) { return it.key === added.key; })[0] || (cart.items || []).filter(function (it) {
          return String(it.variant_id) === String(variantId) && it.properties && it.properties._towsons_personaliser === props._towsons_personaliser;
        })[0];
        if (mine) { self.key = mine.key; self.quantity = mine.quantity; self.el.setAttribute('data-key', mine.key); if (mine.properties && mine.properties._preview) props._preview = mine.properties._preview; }
        self.properties = props;
        self.lastSaved = snap;
        self.dirtySinceRefresh = true;
        self.reflectProperties(props);
        // The basket line shows the new frame now, from memory; the CDN copy
        // is fetched in the background so the redraw can swap to it with no gap.
        var frame = cacheGet(previewCacheKey(self.cfg.template_id, self.cfg.change_id, values, 0, blank, treatName));
        if (frame && self.img) { self.img.removeAttribute('srcset'); self.img.removeAttribute('sizes'); self.img.src = frame; }
        if (props._preview) { try { var warm = new Image(); warm.src = props._preview; } catch (e) {} }
      })
      .catch(function (e) {
        self.setError('Could not save your changes (' + (e && e.message ? e.message : 'error') + '). Please try again.');
      })
      .then(function () { self.saving = null; });
    return this.saving;
  };
  // Warehouse redraws its drawer and cart page on cart:refresh; a theme that
  // does not gets a reload.
  LineEditor.prototype.refreshBasket = function () {
    var self = this;
    this.dirtySinceRefresh = false;
    if (themeFamily() === 'horizon') announceCartChange();
    else { try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } })); } catch (e) {} }
    setTimeout(function () { if (document.body.contains(self.el) && self.el.__towsonsLineEditor === self) window.location.reload(); }, 2500);
  };

  // ─────────────────────────────────────────────────────────────────────
  // THE BASKET ON HORIZON. Warehouse's cart snippets render the line markers
  // (towsons-personaliser-line) in the line itself; Horizon's are Shopify's,
  // untouched. So the markers come from the portal's own section —
  // sections/towsons-cart-lines.liquid, the same snippet for every line in
  // the basket — asked for with Shopify's section rendering, and each line's
  // two parts are put into Horizon's row of the same key: the picture's into
  // its media cell, "Edit" into its details. From there it is the Warehouse
  // path: paintLine draws the mug, LineEditor opens the editor. Horizon
  // redraws its basket after every change (and takes our parts with it), so
  // a row is done again whenever it is new; the section is asked for again
  // only when the basket's lines have changed.
  // ─────────────────────────────────────────────────────────────────────
  var BASKET = { sig: null, parts: null, loading: null };
  function sectionsUrl(names) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + 'cart?sections=' + encodeURIComponent(names);
  }
  function basketRows() {
    return Array.prototype.slice.call(document.querySelectorAll('.cart-items__table-row[data-key]'));
  }
  function decorateBasket() {
    if (themeFamily() !== 'horizon') return;
    var all = basketRows();
    var todo = all.filter(function (r) { return r.getAttribute('data-towsons-line-done') !== r.getAttribute('data-key'); });
    if (!todo.length) return;
    var sig = all.map(function (r) { return r.getAttribute('data-key'); }).sort().join('|');
    if (BASKET.sig === sig && BASKET.parts) { placeParts(todo, BASKET.parts); return; }
    if (BASKET.loading === sig) return;
    BASKET.loading = sig;
    // No "Accept: application/json": asked that way, /cart answers with the basket itself (cart.js) and the
    // sections are never rendered — nothing came back to place (seen on the WeDoMugs trial, 12 Oct 2026).
    fetch(sectionsUrl('towsons-cart-lines'), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var html = (j && j['towsons-cart-lines']) || '';
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var parts = {};
        Array.prototype.forEach.call(doc.querySelectorAll('[data-towsons-cart-line]'), function (el) {
          parts[el.getAttribute('data-towsons-cart-line')] = { image: el.querySelector('[data-part="image"]'), edit: el.querySelector('[data-part="edit"]') };
        });
        BASKET.sig = sig;
        BASKET.parts = parts;
        BASKET.loading = null;
        decorateBasket();
      })
      .catch(function () { BASKET.loading = null; });
  }
  function placeParts(rows, parts) {
    rows.forEach(function (row) {
      var key = row.getAttribute('data-key');
      row.setAttribute('data-towsons-line-done', key);
      var p = parts[key];
      if (!p) return;
      var media = row.querySelector('.cart-items__media-container') || row.querySelector('.cart-items__media');
      if (media && p.image && p.image.children.length) media.appendChild(document.importNode(p.image, true));
      var details = row.querySelector('.cart-items__details');
      if (details && p.edit && p.edit.children.length) details.appendChild(document.importNode(p.edit, true));
    });
    paintLines();
  }
  // Horizon's basket, drawer and count redraw on Shopify's standard cart
  // event: told the lines changed (an edit saved), they read the basket again.
  function announceCartChange() {
    var p = fetch(cartUrl('cart'), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return { cart: { totalQuantity: cart && cart.item_count != null ? cart.item_count : 0 }, detail: { source: 'towsons-personaliser' } }; });
    var e;
    try { e = new Event('shopify:cart:lines-update', { bubbles: true }); } catch (x) { return; }
    e.action = 'update';
    e.context = 'cart';
    e.lines = [];
    e.promise = p;
    p.catch(function () {});
    document.dispatchEvent(e);
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-towsons-personaliser]'), function (root) {
      if (root.__towsonsPersonaliser) return;
      root.__towsonsPersonaliser = new Personaliser(root);
    });
    paintLines();
    decorateBasket();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  // Themes re-render sections (variant change, quick view, the cart drawer
  // after every change) — pick up new mounts and fresh basket lines.
  document.addEventListener('shopify:section:load', init);
  if ('MutationObserver' in window && document.body) {
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      // A timer, not an animation frame: frames stop in a hidden tab, and a
      // basket line redrawn while the shopper is elsewhere must still be
      // painted by the time they look.
      setTimeout(function () { scheduled = false; init(); }, 0);
    }).observe(document.body, { childList: true, subtree: true });
  }
  // The theme redraws the gallery after a variant change; give it a moment,
  // then follow it onto the images it has just shown.
  document.addEventListener('variant:changed', function () {
    setTimeout(function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-towsons-personaliser]'), function (root) {
        if (root.__towsonsPersonaliser) root.__towsonsPersonaliser.reclaimGallery();
      });
    }, 220);
  });

  // Horizon (and any theme on Shopify's standard events): the variant picker
  // announces the colour, and its promise brings the variant once the theme
  // has the new page part — by then Horizon is putting up the new gallery, so
  // the block follows a moment later. A product card's picker (a collection
  // row, a quick add) is another product's, never this page's.
  document.addEventListener('shopify:product:select', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('product-card, quick-add-component, .quick-add-modal')) return;
    var product = e.product && e.product.id ? variantNumber(e.product.id) : '';
    var p = e.promise;
    if (!p || typeof p.then !== 'function') return;
    p.then(function (r) {
      var v = r && r.variant;
      setTimeout(function () {
        Array.prototype.forEach.call(document.querySelectorAll('[data-towsons-personaliser]'), function (root) {
          var me = root.__towsonsPersonaliser;
          if (!me || me.family !== 'horizon') return;
          if (product && me.productKey && variantNumber(me.productKey) !== product) return;
          if (v && v.id) me.switchVariant(v.id); else me.reclaimGallery();
        });
      }, 60);
    }, function () { /* superseded by the next choice */ });
  }, true);
  // A screen-size change can swap which gallery is showing (Horizon's grid and
  // its phone slideshow): the views move to the one now on screen.
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-towsons-personaliser]'), function (root) {
        var me = root.__towsonsPersonaliser;
        if (me && me.family === 'horizon') me.reclaimGallery();
      });
    }, 250);
  });

  // The treats dropdown with the options is the theme's to redraw, so the
  // change is caught at the document and handed to whichever personaliser is
  // on the page. The modal's own dropdown is wired by the modal.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('[data-treats-value]') || (t.closest && t.closest('[data-line-mode]'))) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-towsons-personaliser]'), function (root) {
      if (root.__towsonsPersonaliser) root.__towsonsPersonaliser.onTreatChange();
    });
  });

  window.TowsonsPersonaliser = { init: init, paintLines: paintLines, themeFamily: themeFamily, variantNumber: variantNumber, decorateBasket: decorateBasket, announceCartChange: announceCartChange, drawCylindrical: drawCylindrical, previewCacheKey: previewCacheKey, Renderer: Renderer, treatSpotFor: treatSpotFor, rimFromRgba: rimFromRgba, drawTreat: drawTreat, easeOutBack: easeOutBack, wordsFor: wordsFor, preview: preview, prefillFrom: prefillFrom };
})();
